/* AG Stealth Adjustable Kettlebell landing page v2, journey orchestration.
   GSAP ScrollTrigger (self-hosted agkb-gsap.min.js + agkb-scrolltrigger.min.js)
   drives the hero push-in/dip, film scrub easing, caption/chapter windows, the
   sequence bar, header state, journey rail and card entrances. Frame swapping
   is our own renderer: canvas + ImageBitmap (off-main-thread decodes into a
   memory-capped window we own; one drawImage per step) with the img-swap
   engine as fallback. A geometry cache keeps layout reads off the hot path
   and an idle network queue primes every frame into the HTTP cache without
   competing with scrolling. If GSAP is missing the fallback runs everything
   from one scroll listener, the page never breaks. */
(function () {
  window.__agkbEngine = 'journey-v17.54';
  var root = document.querySelector('.agkb');
  if (!root) return;

  var frameBase = root.dataset.frameBase || '';
  /* ☠ Films come from the liquid as FINGERPRINTED asset_url values. Building
     the path from frameBase instead yields an unversioned CDN URL, and those
     are served with a one-year max-age - so a re-encoded film would sit on
     the theme, byte-verified, and still never reach a returning visitor.
     Falls back to the old construction if the attribute is ever missing. */
  var FILM_URLS = {};
  try { FILM_URLS = JSON.parse(root.dataset.filmUrls || '{}'); } catch (eFU) { FILM_URLS = {}; }

  /* 360 viewer (section 08). model-viewer is ~210 KB brotli and the only
     thing on the page that needs it, so it is NOT a script tag in the
     liquid - it is injected when the section comes within one and a half
     viewports. Until the module defines <model-viewer>, the element is an
     unknown tag and its loading="lazy" never fires, so gating the module
     gates the GLB too. No observer support = load it straight away.
     ☠ This sits ABOVE the GSAP/fallback split on purpose. The GSAP branch
     ends in a bare `return`, so anything added to the tail of this file
     runs ONLY when GSAP is missing - i.e. never, on the live page, and
     silently, with no console error to show for it. */
  (function () {
    var vsec = root.querySelector('[data-agkb="viewer"]');
    if (!vsec) return;
    var mvSrc = vsec.dataset.mvSrc;
    if (!mvSrc) return;
    var asked = false;
    function loadMv() {
      if (asked) return;
      asked = true;
      var s = document.createElement('script');
      s.type = 'module';
      s.src = mvSrc;
      document.head.appendChild(s);
    }
    if (!('IntersectionObserver' in window)) { loadMv(); return; }
    var mvIo = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (entries[i].isIntersecting) { mvIo.disconnect(); loadMv(); return; }
      }
    }, { rootMargin: '150% 0px' });
    mvIo.observe(vsec);
  })();
  /* The whole journey is driven by ONE geometric point per section:
     coverOf() = the fraction of a section's range where the next section
     begins fading over it. Films complete their motion exactly there;
     captions flip at HALF of it, so each caption owns exactly half the
     film's frames; anchors rest just before the flip and just before the
     cover - which makes every anchor-to-anchor beat identical by
     construction: (H - vh) / 2 of scroll. */
  var clamp = function (v, min, max) { return Math.min(max, Math.max(min, v)); };

  /* Geometry cache: every per-frame callback reads from here instead of
     forcing layout mid-scroll. Refreshed on load, resize and ScrollTrigger
     refresh - one class of micro-hitches removed entirely. */
  var GEO = { vh: window.innerHeight, max: 0, map: {}, byEl: (typeof WeakMap === 'function' ? new WeakMap() : null) };
  /* All layout heights in the CSS are svh (toolbar-stable). innerHeight is
     NOT - iOS grows it ~7% when the toolbar collapses mid-scroll, which
     would stretch every beat anchor under the reader's finger. Measure the
     svh unit itself via a probe so JS geometry equals CSS geometry in every
     toolbar state; browsers without svh fall back to innerHeight. */
  var vhProbe = document.createElement('div');
  vhProbe.style.cssText = 'position:fixed;top:0;left:0;width:1px;height:100svh;visibility:hidden;pointer-events:none;';
  (document.body || document.documentElement).appendChild(vhProbe);
  function geoRefresh() {
    GEO.vh = vhProbe.offsetHeight || window.innerHeight;
    GEO.max = document.documentElement.scrollHeight - GEO.vh;
    ['hero', 'films', 'specs', 'choose', 'viewer', 'closer'].forEach(function (name) {
      var el = root.querySelector('[data-agkb="' + name + '"]');
      if (!el) return;
      var g = {
        top: el.getBoundingClientRect().top + window.pageYOffset,
        height: el.offsetHeight,
        cover: 1 - GEO.vh / el.offsetHeight
      };
      GEO.map[name] = g;
      if (GEO.byEl) GEO.byEl.set(el, g);
    });
  }
  function coverOf(section) {
    var g = GEO.byEl && GEO.byEl.get(section);
    return g ? g.cover : 1 - window.innerHeight / section.offsetHeight;
  }
  /* Snap anchors rest this far (in section progress) before the caption
     flip and the section end. Films complete their motion AT the anchor
     and hold, so every rest shows a chapter's true final frame and the
     seq bar reads exactly half at the first snap, full at the second. */
  var ANCHOR_BACK = 0.025;
  geoRefresh();
  addEventListener('resize', geoRefresh);
  addEventListener('orientationchange', geoRefresh);
  addEventListener('load', geoRefresh);
  setTimeout(geoRefresh, 1500);
  setTimeout(geoRefresh, 4000);
  var easeInOut = function (t) { return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2; };

  /* --- Frame films: smoothed scrubbing over sequential JPEGs ---
     Scroll input arrives in discrete wheel/touch steps; mapping it straight to
     frame numbers teleports several frames per tick and reads as jitter. Each
     film therefore lerps its progress towards the scroll target (~120ms
     settle) in its own rAF loop, gliding through the intermediate frames.
     Secondary images (the blurred backdrop) update every frame on landscape
     screens, where the backdrop IS most of the picture; elsewhere every 3rd
     frame to avoid re-rasterising a 30px blur at full rate. */
  /* --- Frame films: smoothed, load-gated scrubbing over sequential JPEGs ---
     Two hard rules learned in production:
     1. Scroll input is discrete, so each film lerps towards its scroll target
        (~250ms settle) and never renders a step of more than 2 frames.
     2. The visible img only ever swaps to a frame that is FULLY LOADED
        (held Image refs + decode gating). If the wanted frame is still in
        flight the film holds its last ready frame and catches up when it
        lands - a blank/black flash is impossible by construction. */
  var backdropEveryFrame = window.matchMedia && window.matchMedia('(min-aspect-ratio:1/1)').matches;
  var IS_PHONE = navigator.maxTouchPoints > 0 && !backdropEveryFrame;
  var CANVAS_OK = typeof createImageBitmap === 'function';
  /* The films are scrub-friendly all-intra MP4s driven by
     video.currentTime - same beat math, hardware decode, a fraction of
     the bytes of the frame sequences. ?filmframes resurrects the frame
     engine as a comparison/fallback lever. */
  var FILM_VIDEO = !/[?&#]filmframes/.test(location.href);
  /* WebKit ships OffscreenCanvas 2D whose transferToImageBitmap can yield
     BLANK bitmaps - films render black with no errors. Prove the pipeline
     with a red pixel at startup; if it comes back wrong, decode-with-resize
     instead (Safari handles that fine). */
  var OSC_PROVEN = false;
  try {
    if (typeof OffscreenCanvas === 'function') {
      var oscT = new OffscreenCanvas(1, 1);
      var oscCtx = oscT.getContext('2d');
      oscCtx.fillStyle = '#f00';
      oscCtx.fillRect(0, 0, 1, 1);
      var oscBm = oscT.transferToImageBitmap();
      var chk = document.createElement('canvas');
      chk.width = chk.height = 1;
      var chkCtx = chk.getContext('2d');
      chkCtx.drawImage(oscBm, 0, 0);
      var px = chkCtx.getImageData(0, 0, 1, 1).data;
      OSC_PROVEN = px[0] > 200 && px[3] > 200;
      oscBm.close();
    }
  } catch (eOsc) { OSC_PROVEN = false; }


  /* --- Bandwidth choreography ------------------------------------------
     After load the whole journey's frames are fetched into the HTTP cache
     during idle - keyframes of every film first, then a full backfill in
     journey order (phones stay on the keyframe diet). The queue PAUSES
     whenever the user scrolls, so the network never competes with frames
     the playhead needs right now. These fetches prime the cache only;
     decoding happens on demand near the playhead. */
  var netQueue = [];
  var netBusy = 0;
  var netHoldUntil = 0;
  var filmActive = 0;   /* films mid-catchup own the network; priming waits */
  /* Every fetch here carries a hard timeout: one hung connection must
     never pin a lane (or a frame) forever. */
  function fetchWithTimeout(u, opts, ms) {
    var ac = typeof AbortController === 'function' ? new AbortController() : null;
    if (ac) opts.signal = ac.signal;
    var timer = ac ? setTimeout(function () { ac.abort(); }, ms) : null;
    var clear = function (x) { if (timer) clearTimeout(timer); return x; };
    var pr;
    try { pr = fetch(u, opts); }
    catch (e) { pr = fetch(u); }
    return pr.then(clear, function (err) { clear(); throw err; });
  }
  var netInflight = [];
  function netPump() {
    /* Background filling never fully stops - during scrolling or active
       film catch-up it drops to a trickle so the playhead's own fetches
       own the bandwidth, then opens back up when things go quiet. */
    var busyCap = (filmActive > 0 || performance.now() < netHoldUntil) ? 2 : (IS_PHONE ? 6 : 10);
    while (netBusy < busyCap && netQueue.length) {
      (function (u) {
        netBusy += 1;
        var rec = { u: u, yielded: false, ac: typeof AbortController === 'function' ? new AbortController() : null };
        netInflight.push(rec);
        var timer = rec.ac ? setTimeout(function () { rec.ac.abort(); }, 8000) : null;
        var done = function () {
          if (timer) clearTimeout(timer);
          var at = netInflight.indexOf(rec);
          if (at !== -1) netInflight.splice(at, 1);
          netBusy -= 1;
          if (rec.yielded) netQueue.unshift(rec.u);
          netPump();
        };
        var opts = { priority: 'low' };
        if (rec.ac) opts.signal = rec.ac.signal;
        var pr;
        try { pr = fetch(u, opts); }
        catch (e) { pr = fetch(u); }
        pr.then(done, done);
      })(netQueue.shift());
    }
  }
  /* The playhead owns the network: the moment a film needs frames, every
     in-flight prime fetch is aborted and requeued for later. */
  function netYield() {
    for (var i = 0; i < netInflight.length; i += 1) {
      netInflight[i].yielded = true;
      if (netInflight[i].ac) netInflight[i].ac.abort();
    }
  }
  function netHold() {
    netHoldUntil = performance.now() + 400;
    setTimeout(netPump, 450);
  }
  addEventListener('scroll', netHold, { passive: true });

  var filmRegistry = [];
  function netPromote(prefix) {
    if (!netQueue.length) return;
    var mine = [], rest = [];
    for (var i = 0; i < netQueue.length; i += 1) {
      (netQueue[i].indexOf(prefix) !== -1 ? mine : rest).push(netQueue[i]);
    }
    if (mine.length) netQueue = mine.concat(rest);
  }
  var JOURNEY_ORDER = ['agkb-replace4', 'agkb-lock4', 'agkb-work4', 'agkb-secure4', 'agkb-ergo5', 'agkb-cast5'];
  function journeyRank(f) {
    for (var i = 0; i < JOURNEY_ORDER.length; i += 1) {
      if (f.prefix && f.prefix.indexOf(JOURNEY_ORDER[i]) === 0) return i;
    }
    return JOURNEY_ORDER.length;
  }
  var journeyQueued = false;
  function queueJourney() {
    if (journeyQueued || FILM_VIDEO) return;
    journeyQueued = true;
    var conn = navigator.connection || {};
    if (conn.saveData || /(^|-)2g/.test(conn.effectiveType || '')) return;
    /* Constrained links get keyframes only - full priming is a broadband
       luxury, not a tax everyone pays. */
    /* Everyone gets the full journey, in three passes of rising density:
       1. keyframes + posters of every film  - scrubbable in seconds
       2. every second frame                 - smooth 25fps-equivalent
       3. the rest                           - full 50fps silk
       Only Save-Data/2G opt-outs skip the backfill (handled above).
       navigator.connection.downlink is never consulted - it learns from
       recent transfers and one heavy visit poisons it low for good. */
    var films = filmRegistry.slice().sort(function (a, b) { return journeyRank(a) - journeyRank(b); });
    films.forEach(function (f) {
      for (var i = 0; i < f.count; i += 6) netQueue.push(f.url(i));
      netQueue.push(f.url(f.count - 1));
    });
    films.forEach(function (f) {
      for (var i = 0; i < f.count; i += 2) if (i % 6) netQueue.push(f.url(i));
    });
    films.forEach(function (f) {
      for (var i = 1; i < f.count; i += 2) if (i % 6) netQueue.push(f.url(i));
    });
    netPump();
  }

  function createFilmVideo(chapter, baseName, onFrame) {
    var v = document.createElement('video');
    v.muted = true;
    v.playsInline = true;
    v.setAttribute('playsinline', '');
    v.setAttribute('muted', '');
    v.preload = 'auto';
    v.className = 'agkb-film-cover';
    var srcUrl = FILM_URLS[baseName] || (frameBase + 'agkbv-' + baseName + '.mp4');
    v.src = srcUrl;
    chapter.appendChild(v);
    /* WKWebView ground rules (sim-proven 07-24): preload='auto' SUSPENDS at
       metadata (zero bytes buffered); a paused seek without data never
       fires 'seeked' and does NOT resume the download; loadeddata/seeked
       can therefore never be trusted as "a frame is on glass". The only
       honest paint signal is requestVideoFrameCallback, and the only
       reliable way to make WebKit actually LOAD is a muted play() (allowed
       without a gesture; Low Power Mode rejects it, so a one-shot touch
       listener in journeyFilm re-primes inside a real gesture).
       The poster img under the video shares its side mask - in the feather
       band the video is semi-transparent and the STARTING FRAME ghosts
       through - so it hides ONLY once a frame has truly been presented. */
    var poster = chapter.querySelector('img.agkb-film-cover');
    var posterHidden = false;
    var canRVFC = typeof v.requestVideoFrameCallback === 'function';
    function hidePoster() {
      if (posterHidden || !poster) return;
      posterHidden = true;
      poster.style.opacity = '0';
    }
    function armPaint() {
      if (!canRVFC) return;
      v.requestVideoFrameCallback(function () { hidePoster(); });
    }
    armPaint();
    var primed = false;
    function prime() {
      if (primed || parked) return;
      primed = true;
      var pp;
      try { pp = v.play(); } catch (ePlay) { primed = false; return; }
      if (pp && pp.then) {
        pp.then(function () {
          setTimeout(function () { if (!parked) v.pause(); }, 80);
        }, function () { primed = false; });
      }
    }
    v.addEventListener('loadeddata', function () {
      if (lastSet < 0 && !seekBusy) {
        seekBusy = true;
        seekAt = performance.now();
        lastSet = 0.001;
        try { v.currentTime = 0.001; } catch (eKick) { seekBusy = false; }
      }
    });
    var target = 0, cur = 0, raf = null, dur = 0, lastSet = -1, parked = false;
    var touring = false;
    var held = false;
    /* Seeks are gated on 'seeked': flooding currentTime aborts the in-flight
       seek before it paints, which forward decode tolerates but BACKWARD
       scrubbing does not - the frame freezes while scrolling up. One seek in
       flight at a time means every issued frame actually renders, in both
       directions. The 300ms watchdog unwedges a seek WebKit drops. */
    var seekBusy = false, seekAt = 0;
    v.addEventListener('loadedmetadata', function () { dur = v.duration || 0; });
    v.addEventListener('seeked', function () {
      if (!canRVFC) hidePoster();
      seekBusy = false;
      if (!raf && dur && !parked && Math.abs(cur * dur - lastSet) > 0.02) {
        raf = requestAnimationFrame(tick);
      }
    });
    function tick() {
      if (touring) { raf = null; return; }
      var d = target - cur;
      if (Math.abs(d) < 0.001) cur = target;
      else cur += d * 0.14;
      if (dur) {
        var now = performance.now();
        if (!seekBusy || now - seekAt > 300) {
          var t = cur * dur;
          if (Math.abs(t - lastSet) > 0.02) {
            seekBusy = true;
            seekAt = now;
            try { v.currentTime = t; } catch (eSeek) { seekBusy = false; }
            lastSet = t;
            if (onFrame) onFrame(cur);
          }
        }
      }
      if (cur === target && (!dur || Math.abs(cur * dur - lastSet) <= 0.02)) raf = null;
      else raf = requestAnimationFrame(tick);
    }
    var self = {
      prefix: 'agkbv-' + baseName,
      count: 1,
      url: function () { return srcUrl; },
      lastActive: 0,
      resize: function () {},
      ready: function () { return v.readyState >= 3; },
      duration: function () { return dur; },
      setProgress: function (p) {
        target = clamp(p, 0, 1);
        self.lastActive = performance.now();
        if (touring && dur) {
          if (held) return;
          /* Tour legs PLAY the film - the native pipeline, no seek churn
             (seek-scrubbing lerp stutters at leg start and tail). A small
             playbackRate servo keeps the clock locked to the scroll. */
          var err = target * dur - v.currentTime;
          var rate = 1 + err * 2;
          if (rate < 0.5) rate = 0.5; else if (rate > 2) rate = 2;
          try { v.playbackRate = rate; } catch (eRate) {}
          if (v.paused) { try { v.play(); } catch (ePlayT) {} }
          if (onFrame) onFrame(clamp(dur ? v.currentTime / dur : 0, 0, 1));
          return;
        }
        if (!raf) raf = requestAnimationFrame(tick);
      },
      tourPlay: function () { touring = true; held = false; },
      tourHold: function (hold) {
        if (!touring) return;
        held = hold;
        try { if (hold) v.pause(); else v.play(); } catch (eHold) {}
      },
      tourStop: function () {
        if (!touring) return;
        touring = false;
        held = false;
        try { v.pause(); v.playbackRate = 1; } catch (eStopT) {}
        if (dur) {
          cur = clamp(v.currentTime / dur, 0, 1);
          target = cur;
          lastSet = v.currentTime;
        }
      },
      warm: function () { prime(); },
      prime: prime,
      park: function () {
        if (parked) return;
        parked = true;
        seekBusy = false;
        primed = false;
        if (poster) { poster.style.opacity = ''; posterHidden = false; }
        v.removeAttribute('src');
        v.load();
        dur = 0;
        lastSet = -1;
      },
      unpark: function () {
        if (!parked) return;
        parked = false;
        seekBusy = false;
        primed = false;
        v.src = srcUrl;
        v.load();
        armPaint();
        prime();
      },
      purge: function () {}
    };
    return self;
  }

  function createFilm(images, prefix, count, onFrame, easeFn) {
    if (CANVAS_OK) return createFilmCanvas(images, prefix, count, onFrame, easeFn);
    return createFilmLegacy(images, prefix, count, onFrame, easeFn);
  }

  function createFilmLegacy(images, prefix, count, onFrame, easeFn) {
    if (!images.length) return null;
    var primary = images[0];
    var secondaries = images.slice(1);
    var secondaryStep = backdropEveryFrame ? 1 : 3;
    /* Sub-frame crossfade layer: a clone of the primary sits on top showing the
       NEXT frame at fractional opacity, so motion reads continuous between
       frames instead of stepping. Cloned so every mask/filter matches. */
    var overlay = primary.cloneNode(false);
    overlay.removeAttribute('data-film-img');
    overlay.removeAttribute('data-chapter-film');
    overlay.removeAttribute('data-chapter-count');
    overlay.setAttribute('alt', '');
    overlay.setAttribute('aria-hidden', 'true');
    overlay.style.opacity = '0';
    primary.parentNode.insertBefore(overlay, primary.nextSibling);

    var target = 0, current = 0, base = 0, overlayFrame = -1, secondaryFrame = 0, running = false, warmed = false;
    var cache = new Array(count), ready = new Array(count);
    function url(i) { return frameBase + prefix + String(i + 1).padStart(5, '0') + '.jpg'; }
    function ensure(i) {
      if (i < 0 || i >= count || cache[i]) return;
      var im = new Image();
      cache[i] = im;
      var mark = function () { ready[i] = true; };
      im.src = url(i);
      if (im.decode) im.decode().then(mark, mark);
      else im.onload = mark;
    }
    ensure(0);
    function setBase(frame) {
      if (frame !== base) {
        base = frame;
        if (window.__agkbTrace) window.__agkbTrace.push(prefix + frame);
        primary.src = url(frame);
        if (onFrame) onFrame(frame / (count - 1));
      }
      if (secondaries.length && (Math.abs(frame - secondaryFrame) >= secondaryStep || current === target)) {
        secondaryFrame = frame;
        for (var k = 0; k < secondaries.length; k += 1) {
          if (secondaries[k].offsetParent !== null) { secondaries[k].src = url(frame); }
        }
      }
    }
    function setOverlay(frame, frac) {
      if (frame >= 0 && frame < count && ready[frame]) {
        if (frame !== overlayFrame) { overlayFrame = frame; overlay.src = url(frame); }
        overlay.style.opacity = frac.toFixed(3);
      } else {
        overlay.style.opacity = '0';
      }
    }
    function tick() {
      var delta = target - current;
      var settled = Math.abs(delta) < 0.0006;
      if (settled) current = target;
      else current += delta * 0.12;
      var pos = (easeFn || easeInOut)(clamp(current, 0, 1)) * (count - 1);
      var wantedBase = settled ? Math.round(pos) : Math.floor(pos);
      var dir = wantedBase >= base ? 1 : -1;
      var stepTo = Math.abs(wantedBase - base) > 2 ? base + dir * 2 : wantedBase;
      ensure(stepTo);
      ensure(stepTo + dir);
      ensure(stepTo + dir * 2);
      ensure(stepTo + dir * 3);
      var candidate = stepTo;
      while (candidate !== base && !ready[candidate]) candidate -= dir;
      setBase(candidate);
      /* Blend towards the next frame only when the base is on pace; snap the
         blend off once settled so the resting frame stays crisp. */
      if (settled || base !== wantedBase) setOverlay(-1, 0);
      else setOverlay(base + 1, pos - wantedBase);
      if (!settled || base !== wantedBase) requestAnimationFrame(tick);
      else running = false;
    }
    var legacySelf = {
      setProgress: function (p) {
        target = clamp(p, 0, 1);
        if (!running) { running = true; requestAnimationFrame(tick); }
      },
      /* Keyframe-first warming (every 4th frame, then backfill) so an
         interrupted preload still covers the sequence. Skipped on
         constrained connections; frames then load on demand as scrubbed. */
      warm: function () {
        if (warmed) return;
        warmed = true;
        var connection = navigator.connection || {};
        if (connection.saveData || /(^|-)2g/.test(connection.effectiveType || '')) return;
        /* Phones get keyframes only: decoding every frame of every film up
           front is a memory spike iOS answers by killing the tab. The
           load-gated scrub fetches the in-between frames on demand. */
        var passes = (backdropEveryFrame || !navigator.maxTouchPoints) ? [0, 1, 2, 3] : [0];
        passes.forEach(function (pass) {
          for (var i = pass; i < count; i += 4) { ensure(i); }
        });
      }
    };
    filmRegistry.push({ count: count, url: url, prefix: prefix });
    return legacySelf;
  }

  /* --- Canvas + ImageBitmap renderer (the bombproof path) ---------------
     <img src> swapping leaves decoded frames in a browser-owned cache that
     can evict any frame at any moment (iOS does), forcing a mid-scrub
     re-decode on the main thread - a dropped frame nothing upstream can
     prevent. Here frames decode OFF the main thread into bitmaps we own,
     held in a memory-capped sliding window around the playhead, and every
     scrub step is one cheap drawImage. The sub-frame crossfade is a second
     drawImage at fractional alpha on the same canvas. At rest the exact
     frame is re-decoded at full resolution and drawn sharp; motion frames
     use an economy decode nobody can see moving. The img renderer above
     remains the no-createImageBitmap fallback. */
  function createFilmCanvas(images, prefix, count, onFrame, easeFn) {
    if (!images.length) return null;
    var primary = images[0];
    var backdrops = images.slice(1);
    var LOH = IS_PHONE ? 854 : 1140;
    var WINDOW = IS_PHONE ? 24 : 36;
    var PINNED = [0, Math.round((count - 1) / 2), count - 1];

    function makeCanvas(like, scale) {
      var c = document.createElement('canvas');
      c.className = like.className;
      c.setAttribute('aria-hidden', 'true');
      like.parentNode.insertBefore(c, like.nextSibling);
      like.style.display = 'none';
      c.__like = like;
      c.__scale = scale;
      return c;
    }
    var sharp = makeCanvas(primary, Math.min(window.devicePixelRatio || 1, 2));
    var bds = backdrops.map(function (b) { return makeCanvas(b, 0.34); });

    function sizeCanvas(c) {
      var w = c.clientWidth, h = c.clientHeight;
      c.__active = c.offsetParent !== null && w > 0 && h > 0;
      if (!c.__active) return;
      var bw = Math.max(2, Math.round(w * c.__scale));
      var bh = Math.max(2, Math.round(h * c.__scale));
      if (c.width !== bw || c.height !== bh) { c.width = bw; c.height = bh; }
      c.__fit = getComputedStyle(c.__like).objectFit === 'contain' ? 'contain' : 'cover';
    }

    var bm = new Array(count);
    var pending = new Array(count);
    var hi = null;                     /* full-res bitmap for the resting frame */
    var target = 0, current = 0, base = 0, running = false;
    var restTimer = null;
    /* Economy frames = full hardware JPEG decode (fast, parallel) followed
       by a GPU downscale through an OffscreenCanvas - ~2.6ms per frame
       against ~13ms for decode-with-resize, which serialises and starves
       the playhead. No OffscreenCanvas (old Safari): keep full-res bitmaps
       and shrink the window to hold the memory line. */
    var OSC_OK = typeof OffscreenCanvas === 'function';
    if (!OSC_OK) WINDOW = Math.max(8, Math.round(WINDOW / 3));
    var scaler = null;

    function url(i) { return frameBase + prefix + String(i + 1).padStart(5, '0') + '.jpg'; }

    function drawFit(ctx, bmp, cw, ch, fit) {
      var sc = fit === 'contain' ? Math.min(cw / bmp.width, ch / bmp.height) : Math.max(cw / bmp.width, ch / bmp.height);
      var dw = bmp.width * sc, dh = bmp.height * sc;
      ctx.drawImage(bmp, (cw - dw) / 2, (ch - dh) / 2, dw, dh);
    }

    var lastDrawKey = '';
    function draw(frame, nextFrame, frac) {
      var useHi = hi && hi.frame === frame && frac === 0;
      var b0 = useHi ? hi.bitmap : bm[frame];
      if (!b0 || !sharp.__active) return;
      var key = frame + ':' + (frac > 0 ? nextFrame + ':' + frac.toFixed(3) : (useHi ? 'hi' : 'lo'));
      if (key === lastDrawKey) return;
      lastDrawKey = key;
      var ctx = sharp.getContext('2d');
      if (sharp.__fit === 'contain') ctx.clearRect(0, 0, sharp.width, sharp.height);
      drawFit(ctx, b0, sharp.width, sharp.height, sharp.__fit);
      if (frac > 0 && bm[nextFrame]) {
        ctx.globalAlpha = frac;
        drawFit(ctx, bm[nextFrame], sharp.width, sharp.height, sharp.__fit);
        ctx.globalAlpha = 1;
      }
      for (var q = 0; q < bds.length; q += 1) {
        if (bds[q].__active) drawFit(bds[q].getContext('2d'), bm[frame] || b0, bds[q].width, bds[q].height, 'cover');
      }
    }
    function redraw() { lastDrawKey = ''; draw(base, -1, 0); }
    function sizeAll() {
      sizeCanvas(sharp);
      bds.forEach(sizeCanvas);
      redraw();
    }

    function ensure(i) {
      if (i < 0 || i >= count || bm[i] || pending[i]) return;
      pending[i] = 1;
      fetchWithTimeout(url(i), {}, 6000).then(function (r) { return r.blob(); }).then(function (blob) {
        if (!OSC_PROVEN && LOH < 1900) {
          return createImageBitmap(blob, { resizeHeight: LOH, resizeQuality: 'high' })
            .catch(function () { return createImageBitmap(blob); });
        }
        return createImageBitmap(blob);
      }).then(function (big) {
        var out = big;
        if (OSC_PROVEN && OSC_OK && big.height > LOH) {
          if (!scaler) scaler = new OffscreenCanvas(Math.max(2, Math.round(big.width * LOH / big.height)), LOH);
          scaler.getContext('2d').drawImage(big, 0, 0, scaler.width, scaler.height);
          big.close();
          out = scaler.transferToImageBitmap();
        }
        pending[i] = 0;
        bm[i] = out;
        if (!running && i === base) redraw();
      }, function () { pending[i] = 0; });
    }
    ensure(0);

    function evict(around) {
      for (var j = 0; j < count; j += 1) {
        if (bm[j] && Math.abs(j - around) > WINDOW && PINNED.indexOf(j) === -1) {
          bm[j].close();
          bm[j] = null;
        }
      }
    }

    function restSharpen() {
      if (restTimer) clearTimeout(restTimer);
      restTimer = setTimeout(function () {
        var want = base;
        fetchWithTimeout(url(want), {}, 8000).then(function (r) { return r.blob(); }).then(function (blob) {
          return createImageBitmap(blob);
        }).then(function (bitmap) {
          if (base === want && !running) {
            if (hi) hi.bitmap.close();
            hi = { frame: want, bitmap: bitmap };
            redraw();
          } else { bitmap.close(); }
        }, function () {});
      }, 180);
    }

    var self;
    function tick() {
      var delta = target - current;
      var settled = Math.abs(delta) < 0.0006;
      if (settled) current = target;
      else current += delta * 0.12;
      var pos = (easeFn || easeInOut)(clamp(current, 0, 1)) * (count - 1);
      var wantedBase = settled ? Math.round(pos) : Math.floor(pos);
      var dir = wantedBase >= base ? 1 : -1;
      var stepTo = Math.abs(wantedBase - base) > 2 ? base + dir * 2 : wantedBase;
      for (var k = 0; k <= 32; k += 1) ensure(stepTo + dir * k);
      var candidate = stepTo;
      while (candidate !== base && !bm[candidate]) candidate -= dir;
      if (candidate !== stepTo) window.__agkbMisses = (window.__agkbMisses || 0) + 1;
      /* Sparse cache (keyframes land first): never crawl behind the network -
         jump to the closest READY frame toward the playhead. Keyframe playback
         immediately; full density as the backfill arrives. */
      if (candidate === base && wantedBase !== base) {
        for (var kj = wantedBase; kj !== base; kj -= dir) {
          if (bm[kj]) { candidate = kj; break; }
        }
      }
      if (candidate !== base) {
        base = candidate;
        self.lastActive = performance.now();
        if (window.__agkbTrace) window.__agkbTrace.push(prefix + base);
        if (onFrame) onFrame(base / (count - 1));
        evict(base);
      }
      var blend = (!settled && base === wantedBase && bm[base + 1]) ? pos - wantedBase : 0;
      draw(base, base + 1, blend);
      if (!settled || base !== wantedBase) requestAnimationFrame(tick);
      else {
        running = false;
        filmActive = Math.max(0, filmActive - 1);
        redraw();
        restSharpen();
        netPump();
      }
    }

    var parked = false;
    self = {
      count: count,
      url: url,
      prefix: prefix,
      lastActive: 0,
      resize: function () { if (!parked) sizeAll(); },
      /* Off-screen films hold NOTHING: canvas backing store released to
         1x1 and every bitmap closed. iOS kills tabs over its memory
         ceiling - seven full-res canvases was most of the bill. */
      park: function () {
        if (parked) return;
        parked = true;
        sharp.width = 1; sharp.height = 1;
        for (var b = 0; b < bds.length; b += 1) { bds[b].width = 1; bds[b].height = 1; }
        for (var j = 0; j < count; j += 1) {
          if (bm[j]) { bm[j].close(); bm[j] = null; }
        }
        if (hi) { hi.bitmap.close(); hi = null; }
      },
      unpark: function () {
        if (!parked) return;
        parked = false;
        sizeAll();
        ensure(0);
        ensure(count >> 1);
        ensure(count - 1);
        ensure(base);
      },
      purge: function () {
        for (var j = 0; j < count; j += 1) {
          if (bm[j] && PINNED.indexOf(j) === -1) { bm[j].close(); bm[j] = null; }
        }
      },
      setProgress: function (p) {
        target = clamp(p, 0, 1);
        if (!running) {
          running = true;
          filmActive += 1;
          netYield();
          requestAnimationFrame(tick);
        }
      },
      warm: function () {
        for (var j = 0; j <= Math.min(count - 1, 30); j += 1) ensure(j);
      }
    };
    filmRegistry.push(self);
    sizeAll();
    return self;
  }

  /* Idle films release their bitmap windows; a return visit refills from
     the primed HTTP cache in a beat. */
  setInterval(function () {
    var now = performance.now();
    var newest = 0;
    filmRegistry.forEach(function (f) { newest = Math.max(newest, f.lastActive || 0); });
    filmRegistry.forEach(function (f) {
      if (f.purge && f.lastActive && f.lastActive !== newest && now - f.lastActive > 12000) f.purge();
    });
  }, 6000);
  addEventListener('resize', function () {
    filmRegistry.forEach(function (f) { if (f.resize) f.resize(); });
  });
  addEventListener('load', function () {
    setTimeout(function () {
      if ('requestIdleCallback' in window) requestIdleCallback(queueJourney);
      else setTimeout(queueJourney, 400);
    }, 1200);
  });

  /* Every film section carries its own progress bar (shown on mobile,
     hidden on desktop in CSS), mirroring the films' RENDERED position -
     same smoothing, easing and load-gating the eye sees, not raw scroll. */
  function seqBarFor(name) {
    var bar = root.querySelector('[data-agkb="' + name + '"] [data-seq-bar]');
    return bar ? function (p) { bar.style.transform = 'scaleY(' + p.toFixed(4) + ')'; } : null;
  }

  function createWindows(items, activeClass, boundaries) {
    var current = -1;
    return function (p) {
      var idx = 0;
      if (boundaries) {
        while (idx < boundaries.length && p >= boundaries[idx]) idx += 1;
      } else {
        idx = clamp(Math.floor(p * items.length), 0, items.length - 1);
      }
      if (idx === current) return;
      current = idx;
      items.forEach(function (el, i) { el.classList.toggle(activeClass, i === idx); });
    };
  }

  /* --- The film journey: ONE pinned stage, eight chapters, ONE transition
     type. All geometry derives from GEO.vh, so resize just works:
       entryStart = films.top - vh   (the stage begins covering the hero)
       beat       = 1.9vh            (anchor to anchor, the page's pulse)
       anchor A_k = entryStart + (k+1)*beat        k = 0..7
     Chapter k's film plays [S_k, A_k] in full - S_k sits just after the
     previous chapter's rest-and-fade - then holds its final frame for
     FILM_HOLD px and crossfades out over FILM_FADE px while k+1 fades in.
     The stage is pinned for the entire journey (fixed through the entry,
     sticky after), so nothing on screen ever moves: every transition is
     opacity only, and every scroll line between fades drives motion. --- */
  var FILM_BEAT_VH = 1.9;
  var FILM_HOLD = 64;
  var FILM_FADE = 26;
  /* Entry choreography shares the hero's scrub band (entryStart = 0 with the
     current 200svh hero): copy exits 0.30-0.65, the dip blacks out 0.45-0.75,
     and only THEN does film one rise out of the darkness. Starting the stage
     any earlier cuts over a still-lit hero - the "harsh" handoff. */
  var ENTRY_FADE_A = 0.62, ENTRY_FADE_B = 0.96;
  /* Film one starts scrubbing DURING the tail of the stage fade, not
     after it: tying its start to ENTRY_FADE_B squeezed its 3 seconds
     into 0.94vh of scroll - 1.9x the pace of every other film ("film
     one feels shorter"). From 0.70 it gets 1.2vh and plays its opening
     frames while emerging from the dark. */
  var FILM1_SCRUB_START = 0.70;
  var smoothstep = function (t) { return t * t * (3 - 2 * t); };
  function journeyFilm() {
    var section = root.querySelector('[data-agkb="films"]');
    if (!section) return null;
    var stage = section.querySelector('[data-films-stage]');
    var chapterEls = Array.prototype.slice.call(section.querySelectorAll('[data-chapter]'));
    var stepEls = Array.prototype.slice.call(section.querySelectorAll('.agkb-step'));
    var tag = section.querySelector('[data-filmtag]');
    var barEl = section.querySelector('[data-seq-bar]');
    var bar = barEl ? function (v) { barEl.style.transform = 'scaleY(' + v.toFixed(4) + ')'; } : null;
    var N = chapterEls.length;
    if (!N) return null;
    var progress = chapterEls.map(function () { return 0; });
    var warmed = chapterEls.map(function () { return false; });
    var curChapter = 0;
    var films = chapterEls.map(function (chapter, i) {
      var imgs = Array.prototype.slice.call(chapter.querySelectorAll('[data-chapter-film]'));
      imgs.sort(function (a, b) {
        return (a.classList.contains('agkb-film-backdrop') ? 1 : 0) - (b.classList.contains('agkb-film-backdrop') ? 1 : 0);
      });
      if (!imgs.length) return null;
      var frameCb = function (p) {
        progress[i] = p;
        if (bar && i === curChapter) bar(p);
      };
      if (FILM_VIDEO) {
        var baseName = (imgs[0].dataset.chapterFilm || '').replace(/^agkb-/, '').replace(/-$/, '');
        return createFilmVideo(chapter, baseName, frameCb);
      }
      return createFilm(imgs, imgs[0].dataset.chapterFilm, parseInt(imgs[0].dataset.chapterCount, 10) || 60, frameCb);
    });
    /* Low Power Mode rejects gestureless muted play(), so the films may sit
       unloaded until the first real touch - re-prime every live film inside
       that gesture's context, where play() is always allowed. */
    var gesturePrimed = false;
    function gesturePrime() {
      if (gesturePrimed) return;
      gesturePrimed = true;
      films.forEach(function (f) { if (f && f.prime) f.prime(); });
    }
    window.addEventListener('touchstart', gesturePrime, { once: true, passive: true, capture: true });
    window.addEventListener('pointerdown', gesturePrime, { once: true, passive: true, capture: true });
    function geo() {
      var g = GEO.map.films;
      if (!g) { geoRefresh(); g = GEO.map.films; }
      return { top: g.top, entryStart: g.top - GEO.vh, beat: FILM_BEAT_VH * GEO.vh, vh: GEO.vh };
    }
    function anchors() {
      var G = geo();
      var out = [];
      for (var k = 0; k < N; k += 1) out.push(G.entryStart + (k + 1) * G.beat);
      return out;
    }
    /* The stage is pinned with position:fixed for the ENTIRE journey, not
       just the entry: WebKit builds ship position:sticky regressions in
       very tall containers (films render black while computed style still
       says sticky - measured on iOS 26). CSS sticky remains as the base
       for the self-check fallback only. Modes: 'fixed' through entry and
       journey, 'bottom' (absolute at the section foot) while the data
       sheet curtains over, cleared before the entry. */
    var entryFixedOk = null;
    var stageMode = '';
    function pinStage(mode) {
      if (mode === 'fixed' && entryFixedOk === false) mode = '';
      if (mode === stageMode) return;
      stageMode = mode;
      if (mode === 'fixed') {
        stage.classList.add('agkb-pinned');
        stage.style.position = 'fixed';
        stage.style.top = '0';
        stage.style.left = '0';
        stage.style.width = '100%';
        stage.style.bottom = '';
        if (entryFixedOk === null) {
          entryFixedOk = Math.abs(stage.getBoundingClientRect().top) < 3;
          if (!entryFixedOk) { stageMode = ''; pinStage(''); }
        }
      } else if (mode === 'bottom') {
        stage.classList.remove('agkb-pinned');
        stage.style.position = 'absolute';
        stage.style.top = 'auto';
        stage.style.bottom = '0';
        stage.style.left = '0';
        stage.style.width = '100%';
      } else {
        stage.classList.remove('agkb-pinned');
        stage.style.position = '';
        stage.style.top = '';
        stage.style.bottom = '';
        stage.style.left = '';
        stage.style.width = '';
      }
    }
    /* Which film zone is on stage; the page-wide marker resolver reads it.
       -1 means the journey is not the current screen. */
    var curZone = -1;
    function setRail(zone) { curZone = zone; }
    function update() {
      var G = geo();
      var y = window.pageYOffset;
      /* Entry: pinned + the whole stage fades in from the dipped hero */
      var pe = (y - G.entryStart) / G.vh;
      var g = GEO.map.films;
      var pinEnd = g ? g.top + g.height - stage.offsetHeight : Infinity;
      if (y > G.entryStart && y < pinEnd) pinStage('fixed');
      else if (y >= pinEnd) pinStage('bottom');
      else pinStage('');
      if (pe > 0 && pe < 1) {
        var so = smoothstep(clamp((pe - ENTRY_FADE_A) / (ENTRY_FADE_B - ENTRY_FADE_A), 0, 1));
        stage.style.opacity = so.toFixed(3);
      } else if (pe >= 1) {
        if (stage.style.opacity) stage.style.opacity = '';
      } else {
        /* At or above the entry start the stage must NEVER cover the
           hero - at exactly y=0 the old else-branch cleared opacity to 1
           over a pinned stage and swallowed the hero + chapter one. */
        stage.style.opacity = '0';
      }
      /* Chapters: fades first, then film scrub ranges */
      var fadeIn = [];
      for (var k = 0; k < N; k += 1) {
        if (k === 0) { fadeIn.push(1); continue; }
        var prevA = G.entryStart + k * G.beat;
        fadeIn.push(smoothstep(clamp((y - (prevA + FILM_HOLD)) / FILM_FADE, 0, 1)));
      }
      for (var k2 = 0; k2 < N; k2 += 1) {
        var A = G.entryStart + (k2 + 1) * G.beat;
        var fout = k2 === N - 1 ? 1 : 1 - fadeIn[k2 + 1];
        var o = Math.min(fadeIn[k2], fout);
        var chapter = chapterEls[k2];
        var key = o.toFixed(3);
        if (chapter.dataset.o !== key) {
          chapter.dataset.o = key;
          chapter.style.opacity = key;
          chapter.style.visibility = o > 0.001 ? 'visible' : 'hidden';
          chapter.style.pointerEvents = 'none';
        }
        if (films[k2]) {
          /* While a nav glide is driving the page, film seeking and
             un/parking is pure churn - seek spam and play() primes on
             every chapter flown past stutter the glide itself (jittery
             nav clicks). Films stand down and paint on arrival; the
             scrub lerp gives the landing a smooth catch-up. The tour is
             exempt: its films play natively and must keep running. */
          var flying = glideRaf !== null && !tourOn;
          if (!flying) {
            var S = k2 === 0 ? G.entryStart + FILM1_SCRUB_START * G.vh : (A - G.beat) + FILM_HOLD + FILM_FADE;
            var dist = y < S ? S - y : (y > A + FILM_HOLD + FILM_FADE ? y - A : 0);
            if (dist < 0.7 * G.beat) {
              if (films[k2].unpark) films[k2].unpark();
              films[k2].setProgress(clamp((y - S) / (A - S), 0, 1));
            } else if (dist > 1.1 * G.beat && films[k2].park) {
              films[k2].park();
              /* Parking drops the source and clears the film's own primed
                 flag, so the NEXT approach has to warm it again. Leaving
                 this latched was why scrolling UP felt rougher than down:
                 a film re-entered from below unparked but never re-primed,
                 so its seeks landed on undecoded data. */
              warmed[k2] = false;
            } else {
              films[k2].setProgress(clamp((y - S) / (A - S), 0, 1));
            }
            /* Warm on DISTANCE, not on a y-window: the old test (y above S
               and below A) only ever fired while approaching downward, so
               films entered from below got no run-up at all. */
            if (!warmed[k2] && dist < 1.5 * G.vh) { warmed[k2] = true; films[k2].warm(); }
          }
        }
      }
      /* Active chapter: captions, film tag, seq bar, rail zone */
      var idx = 0;
      while (idx < N - 1 && y >= G.entryStart + (idx + 1) * G.beat + FILM_HOLD + FILM_FADE / 2) idx += 1;
      if (idx !== curChapter) {
        curChapter = idx;
        chapterEls.forEach(function (el, i) { el.classList.toggle('is-active', i === idx); });
        stepEls.forEach(function (el, i) { el.classList.toggle('is-active', i === idx); });
        if (tag) tag.textContent = 'FILM / 0' + (1 + (idx >> 1));
        var f = films[idx];
        if (f) { netPromote(f.prefix); netPump(); }
      }
      if (bar) bar(progress[curChapter]);
      setRail((y > G.entryStart + 0.3 * G.vh && y < G.entryStart + N * G.beat + G.vh) ? (idx >> 1) : -1);
    }
    /* See-more: each caption's link glides to the MIDDLE of the next
       chapter's beat (the film mid-motion); the last one leads to the
       data sheet. */
    var seeBtns = Array.prototype.slice.call(section.querySelectorAll('[data-seemore]'));
    seeBtns.forEach(function (btn, k) {
      btn.addEventListener('click', function () {
        var G = geo();
        var target;
        if (k < N - 1) {
          var Anext = G.entryStart + (k + 2) * G.beat;
          var Snext = (Anext - G.beat) + FILM_HOLD + FILM_FADE;
          target = Snext + 0.5 * (Anext - Snext);
        } else {
          target = GEO.map.specs ? GEO.map.specs.top : window.pageYOffset;
        }
        glideTo(target);
      });
    });
    /* Loading cue: the hero's scroll invitation pulses as LOADING THE FILM
       until the opening film's frames are in the HTTP cache (or a few
       seconds pass), then flips to the real call to scroll. */
    var cue = root.querySelector('[data-scrollcue]');
    if (cue) {
      var cueT0 = performance.now();
      var cueTimer = setInterval(function () {
        var got = 0;
        try {
          got = performance.getEntriesByType('resource').filter(function (r) {
            return r.name.indexOf(chapterEls[0].querySelector('[data-chapter-film]').dataset.chapterFilm) !== -1;
          }).length;
        } catch (e) { got = 999; }
        if ((FILM_VIDEO && films[0] && films[0].ready && films[0].ready()) || got >= 24 || performance.now() - cueT0 > 6000) {
          clearInterval(cueTimer);
          cue.classList.remove('is-loading');
          cue.querySelector('span').textContent = 'Scroll to lift';
        }
      }, 300);
    }
    /* --- Guided tour: an auto-scroller through the full journey --------
       Each film leg runs over the video's own duration, so the films play
       at their true speed. Every completed chapter holds for a beat, the
       numbers get a slow pass, and the tour parks on the buying section.
       Any touch, wheel or key press cancels instantly (userInterrupt). */
    var tourOn = false, tourTimer = null, tourBtnEl = null, tourLabel = '';
    var tourPausedF = false, tourCurFilm = null;
    function tourSetPaused(pause) {
      if (!tourOn || tourPausedF === pause) return;
      tourPausedF = pause;
      if (tourBtnEl) tourBtnEl.textContent = pause ? 'Resume' : 'Pause';
      var fcur = tourCurFilm !== null ? films[tourCurFilm] : null;
      if (fcur && fcur.tourHold) fcur.tourHold(pause);
    }
    function tourCancel() {
      if (!tourOn) return;
      tourOn = false;
      tourPausedF = false;
      tourCurFilm = null;
      window.__agkbTourOn = false;
      if (tourTimer) { clearTimeout(tourTimer); tourTimer = null; }
      films.forEach(function (f) { if (f && f.tourStop) f.tourStop(); });
      if (tourBtnEl) tourBtnEl.textContent = tourLabel;
    }
    window.__agkbTourCancel = tourCancel;
    window.__agkbTourPause = tourSetPaused;
    function tourSeg(target, dur, linear, done) {
      var from = window.pageYOffset;
      var dist = target - from;
      if (!dist) { done(); return; }
      glideDirSign = dist > 0 ? 1 : -1;
      /* Accumulated clock with a capped step: a main-thread stall (image
         work, decode hitch) PAUSES the tour instead of fast-forwarding it
         - the freeze-then-jump failure mode. */
      var acc = 0;
      var last = performance.now();
      function step(now) {
        if (!tourOn) { glideRaf = null; glideWroteY = null; return; }
        var dstep = now - last;
        last = now;
        if (tourPausedF) { glideRaf = requestAnimationFrame(step); return; }
        if (dstep > 64) dstep = 64;
        acc += dstep;
        var t = clamp(acc / dur, 0, 1);
        var e = linear ? t : smoothstep(t);
        var y = Math.round(from + dist * e);
        window.scrollTo({ top: y, behavior: 'instant' });
        glideWroteY = y;
        if (t < 1) { glideRaf = requestAnimationFrame(step); }
        else { glideRaf = null; glideWroteY = null; done(); }
      }
      glideRaf = requestAnimationFrame(step);
    }
    function tourRun(steps, i) {
      if (!tourOn) return;
      if (i >= steps.length) { tourCancel(); return; }
      var st = steps[i];
      if (st.dwell !== undefined) {
        tourTimer = setTimeout(function fire() {
          if (!tourOn) return;
          if (tourPausedF) { tourTimer = setTimeout(fire, 250); return; }
          tourTimer = null;
          tourRun(steps, i + 1);
        }, st.dwell);
        return;
      }
      if (st.decks) deckResets.forEach(function (fn) { try { fn(); } catch (eDeck) {} });
      if (st.film !== undefined && films[st.film] && films[st.film].tourPlay) {
        tourCurFilm = st.film;
        films[st.film].tourPlay();
      }
      tourSeg(st.to, st.dur, st.linear, function () {
        if (st.film !== undefined && films[st.film] && films[st.film].tourStop) {
          films[st.film].tourStop();
          tourCurFilm = null;
        }
        tourRun(steps, i + 1);
      });
    }
    function tour(btn) {
      if (tourOn) { tourSetPaused(!tourPausedF); return; }
      cancelGlide();
      tourOn = true;
      window.__agkbTourOn = true;
      tourBtnEl = btn || tourBtnEl;
      tourPausedF = false;
      if (tourBtnEl) { tourLabel = tourBtnEl.textContent; tourBtnEl.textContent = 'Pause'; }
      var G = geo();
      var steps = [];
      steps.push({ to: 0, dur: Math.min(1400, 300 + window.pageYOffset * 0.1) });
      steps.push({ dwell: 500 });
      for (var k = 0; k < N; k += 1) {
        var A = G.entryStart + (k + 1) * G.beat;
        var S = k === 0 ? G.entryStart + FILM1_SCRUB_START * G.vh : (A - G.beat) + FILM_HOLD + FILM_FADE;
        steps.push({ to: S, dur: k === 0 ? 1700 : 700 });
        var vdur = films[k] && films[k].duration ? films[k].duration() : 0;
        steps.push({ to: A, dur: Math.max(1500, (vdur || 3) * 1000), linear: true, film: k });
      }
      if (GEO.map.specs) {
        steps.push({ to: GEO.map.specs.top, dur: 2600 });
        steps.push({ dwell: 1800 });
      }
      /* The tour's last leg is the pitch, so it must not arrive on a card
         the carousel happened to cycle to - it was landing on STEALTH 32
         COMING SOON. `decks` resets both carousels to their first card as
         the final glide begins. */
      if (GEO.map.choose) steps.push({ to: GEO.map.choose.top, dur: 2200, decks: true });
      tourRun(steps, 0);
    }
    return { section: section, films: films, update: update, anchors: anchors, tour: tour, tourCancel: tourCancel,
             zone: function () { return curZone; } };
  }
  /* Each carousel registers a "back to the first card" callback here; the
     tour calls them on its final leg. Declared before the journey so the
     tour can reach them, populated later when the decks initialise. */
  var deckResets = [];
  var journey = journeyFilm();

  var header = root.querySelector('[data-agkb-header]');
  var dynamicSection = root.querySelector('[data-agkb="dynamic"]');

  /* Section entries CROSSFADE: while a film section approaches, its stage
     is held over the viewport by transform and fades in above the outgoing
     one - one language of fades everywhere, replacing the slide-over
     curtain. Static sections below keep their natural scroll. */
  /* Header and rail anchors glide to the section top: full speed through
     the journey, then a long deceleration into place (easeOutQuart). Any
     wheel or touch input hands control straight back to the user. */
  var glideRaf = null;
  var glideDirSign = 1;
  var glideWroteY = null;          /* what the glide last wrote, to spot outside interference */
  var settleHoldY = null;          /* set on user cancel: magnet stays off until they really scroll */
  var settleHoldT = 0;
  var settleHoldTimer = null;
  function setHold() {
    settleHoldY = window.pageYOffset;
    settleHoldT = performance.now();
    /* The hold releases on real scrolling - or expires after stillness so
       a stray cancel can never leave the magnet dead. */
    if (settleHoldTimer) clearTimeout(settleHoldTimer);
    if (window.__agkbHoldExpire) settleHoldTimer = setTimeout(window.__agkbHoldExpire, 1300);
  }
  function cancelGlide() {
    if (glideRaf) { cancelAnimationFrame(glideRaf); glideRaf = null; }
    glideWroteY = null;
  }
  function userInterrupt(e) {
    /* A tap ON the tour button must reach its click handler untouched -
       cancelling here killed the tour AND the click then restarted it. */
    if (window.__agkbTourOn && e && e.target && e.target.closest && e.target.closest('[data-tour]')) return;
    if (window.__agkbTourCancel) window.__agkbTourCancel();
    if (glideRaf) setHold();
    cancelGlide();
  }
  var wheelEMA = 0;
  var touchActive = false;
  var TOUCH_DEVICE = navigator.maxTouchPoints > 0;
  addEventListener('wheel', function (e) {
    /* Momentum tails keep emitting decaying wheel events; they are not user
       intent. During a glide only a SPIKE (clearly bigger than the decaying
       tail) or an OPPOSING wheel hands control back - which lets the magnet
       take over mid-momentum without the tail assassinating it. */
    var mag = Math.abs(e.deltaY) + Math.abs(e.deltaX);
    if (mag < 1) return;
    if (window.__agkbTourCancel) window.__agkbTourCancel();
    /* real input hands control back - the event IS the intent (the old
       position-reversal heuristic false-fired on iOS's lagged reads) */
    if (glideRaf) { cancelGlide(); setHold(); }
    if (window.__agkbSettleTick) window.__agkbSettleTick();
    if (glideRaf) {
      var spike = mag >= Math.max(4, wheelEMA * 1.5);
      var opposing = e.deltaY !== 0 && (e.deltaY > 0) !== (glideDirSign > 0);
      if (spike || opposing) userInterrupt();
    }
    wheelEMA = wheelEMA * 0.7 + mag * 0.3;
  }, { passive: true });
  /* While the tour drives, a TAP means "wait": pause in place - the header
     button flips to Resume and continues from exactly here. A DRAG means
     "I'm taking over": cancel outright. A tap that lands on a real control
     (link, button, fold) also cancels, so the control acts on a still
     page. Cancelling used to be the only response, which sent Resume-
     minded users back to the very top. */
  var tourTouch = null;
  addEventListener('touchstart', function (e) {
    touchActive = true;
    if (window.__agkbTourOn && e.touches && e.touches.length === 1) {
      var tgt = e.target;
      if (tgt && tgt.closest && tgt.closest('[data-tour]')) return;   /* the button handles itself */
      tourTouch = {
        x: e.touches[0].clientX, y: e.touches[0].clientY,
        control: !!(tgt && tgt.closest && tgt.closest('a, button, summary, input, [data-buy]'))
      };
      if (window.__agkbTourPause) window.__agkbTourPause(true);
      return;
    }
    userInterrupt(e);
  }, { passive: true });
  addEventListener('touchmove', function (e) {
    if (tourTouch && e.touches && e.touches.length) {
      var tdx = e.touches[0].clientX - tourTouch.x;
      var tdy = e.touches[0].clientY - tourTouch.y;
      if (tdx * tdx + tdy * tdy > 100) {
        tourTouch = null;
        if (window.__agkbTourCancel) window.__agkbTourCancel();
      }
    }
  }, { passive: true });
  addEventListener('keydown', function (e) {
    if (['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' '].indexOf(e.key) !== -1 && glideRaf) {
      cancelGlide();
      setHold();
    }
  });
  addEventListener('touchend', function () {
    touchActive = false;
    if (tourTouch) {
      var wasControl = tourTouch.control;
      tourTouch = null;
      if (wasControl && window.__agkbTourCancel) window.__agkbTourCancel();
    }
  }, { passive: true });
  addEventListener('touchcancel', function () { touchActive = false; tourTouch = null; }, { passive: true });
  addEventListener('keydown', function (e) {
    if ([' ', 'Spacebar', 'ArrowDown', 'ArrowUp', 'PageDown', 'PageUp', 'Home', 'End'].indexOf(e.key) !== -1) userInterrupt();
  }, { passive: true });
  /* Desktop wheel governor: the wheel no longer drives the page raw. Each
     tick feeds a TARGET and the page chases it at a capped speed, so a
     spun wheel cannot blow through a chapter - every film scrubs past at
     a watchable pace. Scrollbar drags and keyboard stay native; foreign
     movement mid-chase hands the page straight back. Writes must be
     behavior:'instant' - the theme's html:focus-within scroll-behavior:
     smooth would otherwise turn each write into a competing animation. */
  var GOV_MAX_V = 2.4;      /* px/ms ceiling for wheel-driven scrolling */
  var GOV_LEAD_VH = 1.4;    /* the target may run this far ahead of the page */
  var govTarget = null, govRaf = null, govLastTs = 0, govWroteY = null;
  function govStop() {
    if (govRaf) { cancelAnimationFrame(govRaf); govRaf = null; }
    govTarget = null; govWroteY = null;
  }
  function govTick(ts) {
    govRaf = null;
    if (govTarget === null) return;
    var y = window.pageYOffset;
    /* scrollbar drag / Home / End moved the page underneath us: theirs */
    if (govWroteY !== null && Math.abs(y - govWroteY) > 24) { govStop(); return; }
    var dt = govLastTs ? Math.min(48, ts - govLastTs) : 16;
    govLastTs = ts;
    var d = govTarget - y;
    if (Math.abs(d) < 1) {
      window.scrollTo({ top: govTarget, behavior: 'instant' });
      govStop();
      if (window.__agkbSettleTick) window.__agkbSettleTick();
      return;
    }
    /* full speed in the open, easing over the last 260px into the target */
    var v = Math.max(0.14, GOV_MAX_V * Math.min(1, Math.abs(d) / 260));
    var wrote = y + Math.min(Math.abs(d), v * dt) * (d < 0 ? -1 : 1);
    window.scrollTo({ top: wrote, behavior: 'instant' });
    govWroteY = wrote;
    govRaf = requestAnimationFrame(govTick);
  }
  if (window.matchMedia && matchMedia('(hover: hover) and (pointer: fine)').matches) {
    addEventListener('wheel', function (e) {
      if (e.ctrlKey || e.defaultPrevented) return;            /* pinch-zoom */
      if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;   /* horizontal */
      if (document.documentElement.style.overflow === 'hidden' ||
          document.body.style.overflow === 'hidden') return;  /* a modal owns the screen */
      if (e.target && e.target.closest && e.target.closest('textarea, select')) return;
      e.preventDefault();
      var dy = e.deltaY;
      if (e.deltaMode === 1) dy *= 16;
      else if (e.deltaMode === 2) dy *= GEO.vh || window.innerHeight;
      var y = window.pageYOffset;
      var lead = GOV_LEAD_VH * (GEO.vh || window.innerHeight);
      var t = clamp((govTarget === null ? y : govTarget) + dy, y - lead, y + lead);
      var maxY = Math.max(0, (document.scrollingElement || document.documentElement).scrollHeight - window.innerHeight);
      govTarget = clamp(t, 0, maxY);
      govWroteY = null;   /* fresh input re-syncs the foreign-movement watch */
      govLastTs = 0;
      if (!govRaf) govRaf = requestAnimationFrame(govTick);
    }, { passive: false });
  }
  function glideTo(y, v0) {
    cancelGlide();
    govStop();
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      window.scrollTo({ top: y, behavior: 'instant' });
      return;
    }
    var from = window.pageYOffset;
    var dist = y - from;
    if (!dist) return;
    glideDirSign = dist > 0 ? 1 : -1;
    var dur = Math.min(2600, 420 + Math.abs(dist) * 0.16);
    /* Nav jumps launch fast and decelerate (easeOutQuart). Settles instead
       pick up at the scroll's own residual velocity and ramp from there -
       a cubic Hermite whose starting slope is the handover speed - so the
       takeover is a continuation of the user's motion, not a lurch. */
    var ease;
    if (v0 !== undefined) {
      dur = Math.min(1600, 480 + Math.abs(dist) * 0.55);
      var m0 = clamp((v0 * dur) / dist, 0, 3);
      ease = function (t) { return (t * t * t - 2 * t * t + t) * m0 + (-2 * t * t * t + 3 * t * t); };
    } else {
      ease = function (t) { return 1 - Math.pow(1 - t, 4); };
    }
    var t0 = null;
    function step(ts) {
      /* Something else moved the page AHEAD of us: native momentum under
         the glide - absorb it and RE-AIM so the landing stays exact.
         Movement BEHIND us is NOT the user fighting: iOS reports scroll
         positions hundreds of px behind instant writes mid-glide (sim
         beacon measured -754px), so lag must be ignored. Real user input
         cancels glides through its own events (touchstart, wheel, keys)
         - no position heuristic needed. */
      if (glideWroteY !== null) {
        var dev = window.pageYOffset - glideWroteY;
        if (Math.abs(dev) > 3 && (dev > 0) === (dist > 0)) {
          from += dev;
          dist -= dev;
        }
      }
      if (t0 === null) t0 = ts;
      var t = clamp((ts - t0) / dur, 0, 1);
      var wrote = from + dist * ease(t);
      window.scrollTo({ top: wrote, behavior: 'instant' });
      /* record what we WROTE - reading back mid-glide returns iOS's
         lagged compositor position, poisoning every comparison */
      glideWroteY = wrote;
      if (t < 1) { glideRaf = requestAnimationFrame(step); }
      else { glideRaf = null; glideWroteY = null; }
    }
    glideRaf = requestAnimationFrame(step);
  }
  /* 360 viewer slots: tap opens a fullscreen, pannable shell that the
     coming 3D renders will drop into. Pointer-drag pans the plane. */
  var viewerSlots = Array.prototype.slice.call(root.querySelectorAll('[data-viewer-slot]'));
  if (viewerSlots.length) {
    var panOverlay = document.createElement('div');
    panOverlay.className = 'agkb-pan-overlay';
    panOverlay.setAttribute('role', 'dialog');
    panOverlay.setAttribute('aria-modal', 'true');
    panOverlay.setAttribute('aria-label', '360 viewer');
    panOverlay.innerHTML =
      '<div class="agkb-pan-stage" data-pan-stage>' +
      '<div class="agkb-pan-plane" data-pan-plane>' +
      '<span class="agkb-index" data-pan-index></span>' +
      '<span class="agkb-pan-title" data-pan-title></span>' +
      '<span class="agkb-soon">3D RENDER COMING SOON</span>' +
      '</div></div>' +
      '<p class="agkb-pan-hint">DRAG TO LOOK AROUND &middot; TAP &times; TO CLOSE</p>' +
      '<button class="agkb-pan-close" type="button" aria-label="Close viewer">&times;</button>';
    document.body.appendChild(panOverlay);
    var panStage = panOverlay.querySelector('[data-pan-stage]');
    var panPlane = panOverlay.querySelector('[data-pan-plane]');
    var panX = 0, panY = 0, dragging = false, dragFromX = 0, dragFromY = 0, lastFocus = null;
    function panApply() {
      var limX = Math.max(0, (panPlane.offsetWidth - window.innerWidth) / 2);
      var limY = Math.max(0, (panPlane.offsetHeight - window.innerHeight) / 2);
      panX = clamp(panX, -limX, limX);
      panY = clamp(panY, -limY, limY);
      panPlane.style.transform = 'translate(calc(-50% + ' + panX + 'px), calc(-50% + ' + panY + 'px))';
    }
    function panOpen(name) {
      panOverlay.querySelector('[data-pan-index]').textContent = name;
      panOverlay.querySelector('[data-pan-title]').textContent = name;
      panX = 0; panY = 0; panApply();
      lastFocus = document.activeElement;
      panOverlay.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      panOverlay.querySelector('.agkb-pan-close').focus();
    }
    function panClose() {
      panOverlay.classList.remove('is-open');
      document.body.style.overflow = '';
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }
    viewerSlots.forEach(function (slot) {
      slot.addEventListener('click', function () { panOpen(slot.dataset.viewerSlot); });
    });
    panOverlay.querySelector('.agkb-pan-close').addEventListener('click', panClose);
    addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && panOverlay.classList.contains('is-open')) panClose();
    });
    panStage.addEventListener('pointerdown', function (e) {
      dragging = true;
      dragFromX = e.clientX - panX;
      dragFromY = e.clientY - panY;
      panStage.classList.add('is-dragging');
      panStage.setPointerCapture(e.pointerId);
    });
    panStage.addEventListener('pointermove', function (e) {
      if (!dragging) return;
      panX = e.clientX - dragFromX;
      panY = e.clientY - dragFromY;
      panApply();
    });
    ['pointerup', 'pointercancel'].forEach(function (ev) {
      panStage.addEventListener(ev, function () {
        dragging = false;
        panStage.classList.remove('is-dragging');
      });
    });
  }

  Array.prototype.forEach.call(root.querySelectorAll('.agkb-header a[href^="#"], .agkb-rail a[href^="#"], a.agkb-btn[href^="#"], a.agkb-seehow[href^="#"]'), function (link) {
    link.addEventListener('click', function (e) {
      // getElementById, not querySelector: an id that starts with a digit
      // (e.g. #8-16kg-kettlebell) is not a valid selector and threw.
      var target = document.getElementById(link.getAttribute('href').slice(1));
      if (!target) return;
      e.preventDefault();
      var y = target.getBoundingClientRect().top + window.pageYOffset;
      /* SEE HOW IT WORKS sits on the hero's SECOND screen, which is already
         level with the films section's own top - so aiming at the section
         top moved the page barely at all, or not at all, and the button
         read as dead. Send it to the first film's caption instead: the
         thing the label is promising. */
      if (link.classList.contains('agkb-seehow') && journey && journey.anchors) {
        var A = journey.anchors();
        if (A && A.length) y = A[0];
      }
      glideTo(y);
      if (history.replaceState) history.replaceState(null, '', link.getAttribute('href'));
    });
  });

  /* --- Idle settle: a soft magnet, never a lock ---------------------------
     While the user scrolls, nothing interferes; slow scrolling scrubs the
     films at exactly their pace. When scrolling goes idle within reach of a
     poster moment (a caption fully on stage, or the top of a content
     section) the page eases onto it and the films settle with it.
     Asymmetric reach: anchors ahead of the travel direction catch from
     further away than anchors behind, so it never fights forward intent.
     Any wheel, touch or key input cancels the settle instantly. */
  var settleEnabled = !(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  if (settleEnabled) {
    function wantMeasure() { return /agkbdebug/.test(location.search + location.hash); }
    var measureMode = wantMeasure();
    var settleTimer = null;
    var settleLastY = window.pageYOffset;
    var settleLastT = 0;
    var settleVel = 0;               /* smoothed px/ms, the handover speed */
    var settleDir = 1;
    var SETTLE_TAIL_SECTIONS = ['specs', 'choose', 'viewer', 'closer'];
    function settleAnchors() {
      if (!GEO.map.films) geoRefresh();
      var film = journey ? journey.anchors() : [];
      var tail = [];
      SETTLE_TAIL_SECTIONS.forEach(function (name) {
        if (GEO.map[name]) tail.push(GEO.map[name].top);
      });
      var max = GEO.max;
      var inRange = function (y) { return y > 0 && y < max - 2; };
      return {
        film: film.filter(inRange).sort(function (a, b) { return a - b; }),
        tail: tail.filter(inRange).sort(function (a, b) { return a - b; })
      };
    }

    window.__agkbHoldExpire = function () {
      if (settleHoldY !== null && performance.now() - settleHoldT >= 1200) {
        settleHoldY = null;
        trySettle();
      }
    };
    function trySettle() {
      if (measureMode || glideRaf || settleHoldY !== null) return;
      var y = window.pageYOffset;
      var vh = GEO.vh;
      /* No idle snapping: the page rests wherever the scroll stops. The
         only settle left is the top edge - scrolling up through the hero
         returns to the very top. The bottom-complete settle is gone: the
         closer + footer are barely over a screen tall, so its capture
         zone swallowed the whole support section and yanked readers to
         the page foot. */
      var heroEnd = GEO.map.films ? GEO.map.films.top - 0.6 * vh : 0;
      if (settleDir < 0 && y > 4 && y < heroEnd) { glideTo(0, settleVel); return; }
    }

    /* --- Anchor debug overlay: add #agkbdebug to the URL ----------------
       Red lines = film posters (the line sits at the viewport centre when
       settled on that anchor). Cyan = content-section tops. HUD shows live
       position, gap fraction and handover velocity. Testing aid only. */
    var dbgWrap = null, dbgHud = null;
    function dbgStart() {
      if (dbgWrap) return;
      dbgWrap = document.createElement('div');
      dbgWrap.style.cssText = 'position:absolute;left:0;top:0;width:100%;height:0;z-index:80;pointer-events:none';
      var dbgNames = ['02.1', '02.2', '03.1', '03.2', '04.1', '04.2', '05.1', '05.2'];
      function dbgDraw() {
        var A = settleAnchors();
        var vh = window.innerHeight;
        dbgWrap.innerHTML = '';
        A.film.concat(A.tail).forEach(function (a, i) {
          var isFilm = i < A.film.length;
          var line = document.createElement('div');
          var label = isFilm ? (dbgNames[i] || '?') : ['06', '07', '08', '09'][i - A.film.length] || '?';
          line.style.cssText = 'position:absolute;left:0;width:100%;border-top:1px dashed ' +
            (isFilm ? '#e10600' : '#00e5ff') + ';top:' + (a + vh / 2) + 'px';
          line.innerHTML = '<span style="position:absolute;right:8px;top:2px;font:700 10px/1 monospace;color:' +
            (isFilm ? '#e10600' : '#00e5ff') + ';background:#0a0a0acc;padding:3px 6px">' + label + ' &middot; ' + Math.round(a) + '</span>';
          dbgWrap.appendChild(line);
        });
      }
      dbgHud = document.createElement('div');
      dbgHud.style.cssText = 'position:fixed;left:10px;bottom:10px;z-index:99;font:700 11px/1.6 monospace;color:#0f0;background:#0a0a0add;padding:8px 10px;pointer-events:none;white-space:pre';
      /* Frame-health meter: dropped rAF %, main-thread long tasks, and how
         often the scrub wanted a frame that was not decoded in time. */
      var jank = { frames: 0, drops: 0, long: 0, worst: 0, lastTs: 0 };
      dbgHud.__jank = jank;
      try {
        new PerformanceObserver(function (list) {
          list.getEntries().forEach(function (e) {
            jank.long += 1;
            jank.worst = Math.max(jank.worst, Math.round(e.duration));
          });
        }).observe({ type: 'longtask' });
      } catch (eo) {}
      document.body.appendChild(dbgWrap);
      document.body.appendChild(dbgHud);
      dbgDraw();
      addEventListener('resize', dbgDraw);
      /* Geometry reads are throttled to 4Hz - at 60Hz they were enough
         layout work to jank scrolling on the full live page. */
      var dbgRun = [], dbgLastMeasure = 0;
      (function dbgTick(ts) {
        if (!dbgWrap) return;
        if (!dbgLastMeasure || ts - dbgLastMeasure > 250) {
          dbgLastMeasure = ts || 1;
          var A = settleAnchors();
          dbgRun = A.film.slice();
          if (A.tail.length) dbgRun.push(A.tail[0]);
        }
        var y = window.pageYOffset, info = 'outside journey';
        if (dbgRun.length > 1 && y > dbgRun[0] && y < dbgRun[dbgRun.length - 1]) {
          var i = 0;
          while (i < dbgRun.length - 2 && dbgRun[i + 1] <= y) i++;
          info = 'gap ' + i + ' f=' + (((y - dbgRun[i]) / (dbgRun[i + 1] - dbgRun[i])) * 100).toFixed(1) + '%';
        }
        var jk = dbgHud.__jank;
        if (jk.lastTs) {
          jk.frames += 1;
          if (ts - jk.lastTs > 26) jk.drops += 1;
        }
        jk.lastTs = ts;
        dbgHud.textContent = window.__agkbEngine + '\ny ' + Math.round(y) + '  ' + info +
          '\nvel ' + settleVel.toFixed(2) + ' px/ms  dir ' + settleDir +
          '\ndrops ' + (jk.frames ? (jk.drops * 100 / jk.frames).toFixed(1) : '0.0') + '%  longtasks ' + jk.long + ' (worst ' + jk.worst + 'ms)' +
          '\nframe misses ' + (window.__agkbMisses || 0) +
          '\nMAGNET OFF - measuring';
        requestAnimationFrame(dbgTick);
      })();
    }
    function dbgStop() {
      if (dbgWrap) { dbgWrap.remove(); dbgWrap = null; }
      if (dbgHud) { dbgHud.remove(); dbgHud = null; }
    }
    if (measureMode) dbgStart();
    addEventListener('hashchange', function () {
      measureMode = wantMeasure();
      if (measureMode) dbgStart(); else dbgStop();
    });

    /* A wheel event resets the idle clock - and REARMS it. Trailing
       momentum ticks that move nothing used to clear the timer and leave
       the magnet dead at the end of real gestures. */
    function settleFire() { trySettle(); }
    window.__agkbSettleTick = function () {
      if (settleTimer) clearTimeout(settleTimer);
      settleTimer = setTimeout(settleFire, 90);
    };

    var settleSpeedPrev = 0;
    var settleDecay = 0;
    addEventListener('scroll', function () {
      var y = window.pageYOffset;
      var prevY = settleLastY;
      var now = performance.now();
      if (Math.abs(y - settleLastY) > 1) settleDir = y > settleLastY ? 1 : -1;
      var dt = now - settleLastT;
      if (dt > 0 && dt < 200 && !glideRaf) {
        settleVel = settleVel * 0.6 + ((y - settleLastY) / dt) * 0.4;
      } else if (!glideRaf) {
        settleVel = 0;
      }
      settleLastY = y;
      settleLastT = now;
      /* A real scroll away from the cancel point re-arms the magnet */
      if (settleHoldY !== null && Math.abs(y - settleHoldY) > 4) settleHoldY = null;
      if (settleTimer) clearTimeout(settleTimer);
      if (glideRaf) return;
      /* Predictive takeover: three consecutive shrinking velocity readings
         under the threshold is decaying momentum, not intent - hand over
         mid-motion at the current speed instead of waiting for the crawl
         to die. A fresh finger or a wheel spike cancels as usual. */
      var speed = Math.abs(settleVel);
      if (speed < settleSpeedPrev - 0.004) settleDecay += 1; else settleDecay = 0;
      settleSpeedPrev = speed;
      if (!touchActive && settleDecay >= 3 && speed > 0.02 && speed < 0.5) {
        settleDecay = 0;
        trySettle();
        if (glideRaf) return;
      }
      settleTimer = setTimeout(settleFire, 90);
    }, { passive: true });
  }

  /* --- Where am I? One resolver, two markers -----------------------------
     The rail dot and the header underline both answer the same question, so
     one function answers it, measured from live positions every scroll.
     The rail used to be split between ScrollTrigger (for the real sections)
     and the journey (for the film zones); the triggers cache their start
     and end pixels, and this page's heights are svh-based and shift when a
     phone's toolbar collapses or the film section's height changes - so the
     rail could stick on a zone and never move again. Measuring beats
     caching here: it is a handful of getBoundingClientRect calls, only when
     the answer might have changed. */
  (function () {
    var navLinks = Array.prototype.slice.call(root.querySelectorAll('.agkb-nav a[href^="#"]'));
    var railLinks = Array.prototype.slice.call(root.querySelectorAll('[data-rail]'));
    if (!navLinks.length && !railLinks.length) return;
    var ZONE_HREF = ['#system', '#movement', '#comfort'];
    var order = [
      { href: '#top', sel: '[data-agkb="hero"]' },
      { href: '#system', sel: '[data-agkb="films"]' },
      { href: '#numbers', sel: '[data-agkb="specs"]' },
      { href: '#models', sel: '[data-agkb="choose"]' },
      { href: '#support', sel: '[data-agkb="closer"]' }
    ].map(function (s) { return { href: s.href, el: root.querySelector(s.sel) }; })
     .filter(function (s) { return !!s.el; });
    var cur = null;
    function resolve() {
      /* Inside the journey the film zone is authoritative - it knows which
         chapter is on the stage, which no element box can tell us. */
      var z = journey && journey.zone ? journey.zone() : -1;
      if (z >= 0) return ZONE_HREF[Math.min(z, ZONE_HREF.length - 1)] || '#system';
      var mid = window.pageYOffset + window.innerHeight * 0.5;
      var found = order.length ? order[0].href : null;
      for (var i = 0; i < order.length; i += 1) {
        var r = order[i].el.getBoundingClientRect();
        if (r.top + window.pageYOffset <= mid) found = order[i].href; else break;
      }
      return found;
    }
    function paint() {
      var href = resolve();
      if (href === cur) return;
      cur = href;
      navLinks.forEach(function (l) {
        var on = l.getAttribute('href') === href;
        l.classList.toggle('is-active', on);
        if (on) l.setAttribute('aria-current', 'true'); else l.removeAttribute('aria-current');
      });
      railLinks.forEach(function (l) {
        var on = l.getAttribute('href') === href;
        l.classList.toggle('is-current', on);
        if (on) l.setAttribute('aria-current', 'true'); else l.removeAttribute('aria-current');
      });
    }
    var queued = false;
    function schedule() {
      if (queued) return;
      queued = true;
      requestAnimationFrame(function () { queued = false; paint(); });
    }
    addEventListener('scroll', schedule, { passive: true });
    addEventListener('resize', schedule);
    paint();
  })();

  /* Back to top: every section carries one. */
  Array.prototype.forEach.call(root.querySelectorAll('[data-totop]'), function (btn) {
    btn.addEventListener('click', function () { glideTo(0, 0); });
  });

  /* Product photos arrive on studio backgrounds (white or grey) that sit
     ugly on the dark cards. Cut the backdrop out AT RUNTIME: redraw via a
     CORS-clean canvas and flood-fill transparency in from the corners,
     keyed to each corner's own colour. Any failure (CORS, decode) leaves
     the original photo untouched - never worse than before. */
  function agkbCutout(imgEl) {
    var src = imgEl.currentSrc || imgEl.src;
    if (!src) return;
    var probe = new Image();
    probe.crossOrigin = 'anonymous';
    probe.onload = function () {
      try {
        var w = probe.naturalWidth, h = probe.naturalHeight;
        if (!w || !h || w * h > 1200000) return;
        var c = document.createElement('canvas');
        c.width = w; c.height = h;
        var x = c.getContext('2d');
        x.drawImage(probe, 0, 0);
        var d = x.getImageData(0, 0, w, h);
        var px = d.data;
        /* Already-transparent art (the cutaway drawing) has nothing to cut.
           Running the fill on it is actively destructive: every pixel shares
           one RGB and only alpha varies, so the flood would sweep the whole
           image and erase the drawing. */
        if (px[3] < 250 || px[(w - 1) * 4 + 3] < 250 ||
            px[((h - 1) * w) * 4 + 3] < 250 || px[((h - 1) * w + w - 1) * 4 + 3] < 250) return;
        /* Light backdrops (white/pale grey) afford a generous tolerance -
           the product is far away in colour. DARK GREY backdrops do not:
           the bell is also dark, so the fill runs tight and adds a soft
           alpha rim where it stops instead of a hard cliff. Leftover deep
           shadows are left alone in dark mode - on the dark cards they
           read as shadow, not as a seam. */
        var TOL = 52;
        var TOL_DARK = 20;
        var SOFT_DARK = 55;   /* rim ramp width (colour distance) past the hard cut */
        var stack = [];
        var seen = new Uint8Array(w * h);
        var sawLight = false, sawDark = false;
        [[0, 0], [w - 1, 0], [0, h - 1], [w - 1, h - 1]].forEach(function (corner) {
          var ci = (corner[1] * w + corner[0]) * 4;
          var cr = px[ci], cg = px[ci + 1], cb = px[ci + 2];
          var csum = cr + cg + cb;
          var cspread = Math.max(cr, cg, cb) - Math.min(cr, cg, cb);
          if (csum >= 240) { sawLight = true; stack.push([corner[0], corner[1], cr, cg, cb, TOL * 3, 0]); }
          else if (csum >= 70 && cspread < 26) { sawDark = true; stack.push([corner[0], corner[1], cr, cg, cb, TOL_DARK * 3, SOFT_DARK]); }
          /* near-black corner: indistinguishable from the product - skip */
        });
        while (stack.length) {
          var it = stack.pop();
          var sx = it[0], sy = it[1], cr = it[2], cg = it[3], cb = it[4], hard = it[5], soft = it[6];
          var idx = sy * w + sx;
          if (seen[idx]) continue;
          seen[idx] = 1;
          var o = idx * 4;
          var dist = Math.abs(px[o] - cr) + Math.abs(px[o + 1] - cg) + Math.abs(px[o + 2] - cb);
          if (dist > hard) {
            /* soft rim: the pixel the fill stops ON fades instead of
               cliffing - covers anti-aliased product edges and the first
               ring of shadow. No spread from here. */
            if (soft && dist <= hard + soft) {
              var ra = Math.round(((dist - hard) / soft) * 255);
              if (ra < px[o + 3]) px[o + 3] = ra;
            }
            continue;
          }
          px[o + 3] = 0;
          if (sx > 0) stack.push([sx - 1, sy, cr, cg, cb, hard, soft]);
          if (sx < w - 1) stack.push([sx + 1, sy, cr, cg, cb, hard, soft]);
          if (sy > 0) stack.push([sx, sy - 1, cr, cg, cb, hard, soft]);
          if (sy < h - 1) stack.push([sx, sy + 1, cr, cg, cb, hard, soft]);
        }
        /* Pass two: enclosed pockets (inside the handle) can't be reached
           from the corners. Clear any LARGE leftover region that matches the
           backdrop colour - small light details (lettering, highlights)
           stay because they never reach the size gate. */
        var refs = [], darkRefs = [];
        [[0, 0], [w - 1, 0], [0, h - 1], [w - 1, h - 1]].forEach(function (corner) {
          var ci = (corner[1] * w + corner[0]) * 4;
          var cs = px[ci] + px[ci + 1] + px[ci + 2];
          var csp = Math.max(px[ci], px[ci + 1], px[ci + 2]) - Math.min(px[ci], px[ci + 1], px[ci + 2]);
          if (cs >= 240) refs.push([px[ci], px[ci + 1], px[ci + 2]]);
          else if (cs >= 70 && csp < 26) darkRefs.push([px[ci], px[ci + 1], px[ci + 2], cs]);
        });
        if (refs.length || darkRefs.length) {
          /* Pockets and drop shadows are DESATURATED AND BRIGHT - grey
             paper, grey shadow. The red band is saturated, the iron is
             dark, and small bright details (lettering, speculars) are
             protected by the size gate, so this signature only ever
             matches leftover backdrop. */
          var isBackdrop = function (o3) {
            var r = px[o3], g = px[o3 + 1], b = px[o3 + 2];
            var mx = Math.max(r, g, b), mn = Math.min(r, g, b);
            if (refs.length && (mx - mn) < 42 && (r + g + b) > 300) return true;
            /* Chroma backdrops (green screen) are saturated: also match
               pockets that sit tight on a corner colour. */
            for (var rk = 0; rk < refs.length; rk += 1) {
              if (Math.abs(r - refs[rk][0]) + Math.abs(g - refs[rk][1]) + Math.abs(b - refs[rk][2]) <= 156) return true;
            }
            /* Dark-grey backdrop pockets: desaturated, close to a corner
               colour, and NOT meaningfully darker than the backdrop - the
               brightness guard is what keeps the bell's lit rubber (which
               is desaturated too, but darker) out of reach. */
            for (var dk = 0; dk < darkRefs.length; dk += 1) {
              if ((mx - mn) < 26 &&
                  (r + g + b) >= darkRefs[dk][3] - 25 &&
                  Math.abs(r - darkRefs[dk][0]) + Math.abs(g - darkRefs[dk][1]) + Math.abs(b - darkRefs[dk][2]) <= 75) return true;
            }
            return false;
          };
          var minRegion = Math.max(1200, (w * h) / 220);
          var comp = new Int32Array(w * h);
          for (var pi = 0; pi < w * h; pi += 1) {
            if (seen[pi] || comp[pi]) continue;
            var o2 = pi * 4;
            if (px[o2 + 3] === 0) continue;
            if (!isBackdrop(o2)) continue;
            var members = [pi];
            comp[pi] = 1;
            var qi = 0;
            while (qi < members.length) {
              var cur = members[qi]; qi += 1;
              var cx = cur % w, cy = (cur / w) | 0;
              [[cx - 1, cy], [cx + 1, cy], [cx, cy - 1], [cx, cy + 1]].forEach(function (nb) {
                if (nb[0] < 0 || nb[0] >= w || nb[1] < 0 || nb[1] >= h) return;
                var ni = nb[1] * w + nb[0];
                if (comp[ni] || seen[ni]) return;
                var no = ni * 4;
                if (px[no + 3] === 0) return;
                if (!isBackdrop(no)) return;
                comp[ni] = 1;
                members.push(ni);
              });
            }
            if (members.length >= minRegion) {
              for (var mi = 0; mi < members.length; mi += 1) px[members[mi] * 4 + 3] = 0;
            }
          }
        }
        /* Pass three: shadows are GRADIENTS - any hard threshold slices
           them into stair-steps. March out from every cleared pixel
           through desaturated neighbours, fading alpha with darkness, so
           leftover shadow dissolves instead of ending in a cliff. The
           march can't leave desaturated territory, so the bell and the
           red band stay solid. LIGHT backdrops only: on a dark backdrop
           "desaturated and dimmer than the paper" describes the bell
           itself, so the march would eat the product - the dark path's
           soft rim in pass one does the smoothing instead. */
        if (!sawLight && sawDark) {
          x.putImageData(d, 0, 0);
          c.toBlob(function (blob) {
            if (blob) imgEl.src = URL.createObjectURL(blob);
          }, 'image/png');
          return;
        }
        var frontier = [];
        for (var fi2 = 0; fi2 < w * h; fi2 += 1) {
          if (px[fi2 * 4 + 3] === 0) frontier.push(fi2);
        }
        var feathered = new Uint8Array(w * h);
        while (frontier.length) {
          var fcur = frontier.pop();
          var fx = fcur % w, fy = (fcur / w) | 0;
          [[fx - 1, fy], [fx + 1, fy], [fx, fy - 1], [fx, fy + 1]].forEach(function (nb) {
            if (nb[0] < 0 || nb[0] >= w || nb[1] < 0 || nb[1] >= h) return;
            var ni = nb[1] * w + nb[0];
            if (feathered[ni]) return;
            var no = ni * 4;
            if (px[no + 3] === 0) return;
            var r = px[no], g = px[no + 1], b = px[no + 2];
            var sum = r + g + b;
            if (Math.max(r, g, b) - Math.min(r, g, b) >= 50 || sum <= 150) return;
            feathered[ni] = 1;
            var t = (sum - 150) / 450;
            if (t > 1) t = 1;
            var a = Math.round((1 - t) * 255);
            if (a < px[no + 3]) px[no + 3] = a;
            frontier.push(ni);
          });
        }
        x.putImageData(d, 0, 0);
        c.toBlob(function (blob) {
          if (blob) imgEl.src = URL.createObjectURL(blob);
        }, 'image/png');
      } catch (eCut) {}
    };
    probe.src = src;
  }
  /* Cutouts crunch hundreds of thousands of pixels - staggered, and never
     while the tour is driving, so they can't stall its clock. */
  (function () {
    var cutQueue = Array.prototype.slice.call(root.querySelectorAll('.agkb-card-img, .agkb-dims img'));
    function cutNext() {
      if (!cutQueue.length) return;
      if (window.__agkbTourOn) { setTimeout(cutNext, 1200); return; }
      agkbCutout(cutQueue.shift());
      setTimeout(cutNext, 400);
    }
    setTimeout(cutNext, 700);
  })();

  /* Hero range tiles carry the deck's desktop hover: one container-level
     pointermove feeds every tile's glow mask (so the red edge lights across
     the shared borders, as on the deck), and each tile tilts under the
     cursor. Angles are gentler than the deck's - these tiles are small, and
     a big tilt on a link you are about to click reads as instability. */
  (function () {
    var tileWrap = root.querySelector('.agkb-hero-tiles');
    if (!tileWrap || !window.matchMedia) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (!window.matchMedia('(hover: hover)').matches) return;
    var deskMq = window.matchMedia('(min-width: 901px)');
    var tiles = Array.prototype.slice.call(tileWrap.children);
    tileWrap.addEventListener('pointermove', function (e) {
      if (!deskMq.matches) return;
      tiles.forEach(function (tile) {
        var r = tile.getBoundingClientRect();
        tile.style.setProperty('--gx', (((e.clientX - r.left) / r.width) * 100).toFixed(1) + '%');
        tile.style.setProperty('--gy', (((e.clientY - r.top) / r.height) * 100).toFixed(1) + '%');
      });
      tileWrap.classList.add('agkb-glow-on');
    });
    tileWrap.addEventListener('pointerleave', function () {
      tileWrap.classList.remove('agkb-glow-on');
      tiles.forEach(function (tile) { tile.style.transform = ''; });
    });
    tiles.forEach(function (tile) {
      tile.addEventListener('pointermove', function (e) {
        if (!deskMq.matches) return;
        var r = tile.getBoundingClientRect();
        var px = (e.clientX - r.left) / r.width, py = (e.clientY - r.top) / r.height;
        tile.style.transform = 'rotateY(' + ((px - 0.5) * 5).toFixed(2) + 'deg) rotateX(' + ((0.5 - py) * 4).toFixed(2) + 'deg)';
        tile.style.setProperty('--mx', (px * 100).toFixed(1) + '%');
        tile.style.setProperty('--my', (py * 100).toFixed(1) + '%');
      });
      tile.addEventListener('pointerleave', function () { tile.style.transform = ''; });
    });
    deskMq.addEventListener && deskMq.addEventListener('change', function (ev) {
      if (!ev.matches) {
        tileWrap.classList.remove('agkb-glow-on');
        tiles.forEach(function (tile) { tile.style.transform = ''; });
      }
    });
  })();

  /* PICK THE RANGE deck: fanned stack that auto-cycles (pausing on hover
     and offscreen), drags, and answers the dots; the front card tilts in
     3D with a following spotlight on pointer devices. */
  Array.prototype.forEach.call(root.querySelectorAll('[data-deck]'), function (deck) {
    var cards = Array.prototype.slice.call(deck.querySelectorAll('[data-deck-card]'));
    var dotsWrap = deck.nextElementSibling && deck.nextElementSibling.hasAttribute && deck.nextElementSibling.hasAttribute('data-deck-dots') ? deck.nextElementSibling : null;
    var dots = dotsWrap ? Array.prototype.slice.call(dotsWrap.querySelectorAll('i')) : [];
    if (cards.length < 2) return;
    var order = cards.map(function (_, i) { return i; });
    var hovering = false, visible = false, timer = null;
    var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var deckMq = window.matchMedia ? window.matchMedia('(max-width: 900px)') : null;
    var deckIsCarousel = deckMq ? deckMq.matches : false;
    if (deckMq && deckMq.addEventListener) {
      deckMq.addEventListener('change', function (ev) {
        if (!ev.matches) {
          /* crossed to desktop: strip stack state so the grid renders clean */
          if (timer) { clearInterval(timer); timer = null; }
          cards.forEach(function (c) { c.className = 'agkb-deck-card'; });
        } else {
          paint();
          arm();
        }
      });
    }
    /* Every handler below attaches ONCE, whatever width the page loads at,
       and gates its behaviour on the live media query. The old shape only
       wired the swipe/arrow/dot handlers when the page LOADED in carousel
       mode - resize a window under 900px (or rotate a tablet) and the deck
       painted as a carousel but was deaf to every input. */
    if (!reduced && window.matchMedia('(hover: hover)').matches) {
      /* Desktop grid: one deck-level listener feeds every card's glow mask,
         so the red edge lights up across neighbouring tiles near the cursor */
      deck.addEventListener('pointermove', function (e) {
        if (deckMq && deckMq.matches) return;
        cards.forEach(function (wrap) {
          var card = wrap.querySelector('.agkb-card');
          var r = card.getBoundingClientRect();
          card.style.setProperty('--gx', (((e.clientX - r.left) / r.width) * 100).toFixed(1) + '%');
          card.style.setProperty('--gy', (((e.clientY - r.top) / r.height) * 100).toFixed(1) + '%');
        });
        deck.classList.add('agkb-glow-on');
      });
      deck.addEventListener('pointerleave', function () { deck.classList.remove('agkb-glow-on'); });
      cards.forEach(function (wrap) {
        var card = wrap.querySelector('.agkb-card');
        card.addEventListener('pointermove', function (e) {
          if (deckMq && deckMq.matches) return;
          var r = card.getBoundingClientRect();
          var px2 = (e.clientX - r.left) / r.width, py = (e.clientY - r.top) / r.height;
          card.style.transform = 'rotateY(' + ((px2 - 0.5) * 7).toFixed(2) + 'deg) rotateX(' + ((0.5 - py) * 5).toFixed(2) + 'deg)';
          card.style.setProperty('--mx', (px2 * 100).toFixed(1) + '%');
          card.style.setProperty('--my', (py * 100).toFixed(1) + '%');
        });
        card.addEventListener('pointerleave', function () { card.style.transform = ''; });
      });
    }
    function paint() {
      order.forEach(function (cardIdx, pos) {
        cards[cardIdx].className = 'agkb-deck-card pos' + Math.min(pos, 2);
      });
      dots.forEach(function (d, di) { d.className = di === order[0] ? 'on' : ''; });
    }
    function advance(dir) {
      if (dir > 0) order.push(order.shift());
      else order.unshift(order.pop());
      paint();
    }
    function arm() {
      if (timer) { clearInterval(timer); timer = null; }
      if (reduced) return;
      if (deckMq && !deckMq.matches) return;   /* grid mode never auto-cycles */
      timer = setInterval(function () {
        if (!hovering && visible && !document.hidden) advance(1);
      }, 3200);
    }
    deck.addEventListener('pointerenter', function () { hovering = true; });
    deck.addEventListener('pointerleave', function () { hovering = false; });
    /* Mouse drags: the browser starts a NATIVE image/text drag the moment
       the pointer moves with the button down, and pointerup never arrives
       - that is why "swipe" felt dead with a mouse. Block the native drag
       and track the gesture ourselves. Touch has its own tracker below,
       so the pointer path is mouse/pen only (on Android both paths fired
       for one swipe and advanced twice). */
    var downX = null, downY = null, dragDist = 0;
    deck.addEventListener('dragstart', function (e) { e.preventDefault(); });
    deck.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'touch') return;
      downX = e.clientX; downY = e.clientY; dragDist = 0;
    });
    deck.addEventListener('pointermove', function (e) {
      if (downX !== null && e.pointerType !== 'touch') {
        dragDist = Math.max(dragDist, Math.abs(e.clientX - downX));
      }
    });
    deck.addEventListener('pointerup', function (e) {
      if (downX === null || e.pointerType === 'touch') return;
      var dx = e.clientX - downX, dy = e.clientY - downY;
      downX = downY = null;
      if (Math.abs(dx) > 36 && Math.abs(dx) > Math.abs(dy)) { advance(dx < 0 ? 1 : -1); arm(); }
    });
    deck.addEventListener('pointercancel', function () { downX = downY = null; });
    /* the click that lands at the end of a real drag must not press a
       BUY button or follow a card link */
    deck.addEventListener('click', function (e) {
      if (dragDist > 12) { dragDist = 0; e.preventDefault(); e.stopPropagation(); }
    }, true);
    /* iOS swallows pointerup behind pointercancel while it arbitrates
       gestures - track touches explicitly so a swipe always lands */
    var tX = null, tY = null, tLastX = null;
    deck.addEventListener('touchstart', function (e) {
      tX = tLastX = e.touches[0].clientX;
      tY = e.touches[0].clientY;
    }, { passive: true });
    deck.addEventListener('touchmove', function (e) {
      if (tX !== null) tLastX = e.touches[0].clientX;
    }, { passive: true });
    deck.addEventListener('touchend', function (e) {
      if (tX === null) return;
      var touch = (e.changedTouches && e.changedTouches[0]) || null;
      var endX = touch ? touch.clientX : tLastX;
      var endY = touch ? touch.clientY : tY;
      var dx = endX - tX, dy = endY - tY;
      tX = tY = tLastX = null;
      if (Math.abs(dx) > 36 && Math.abs(dx) > Math.abs(dy)) {
        advance(dx < 0 ? 1 : -1);
        arm();
      }
    }, { passive: true });
    dots.forEach(function (d, di) {
      function go() { while (order[0] !== di) order.push(order.shift()); paint(); arm(); }
      d.addEventListener('click', go);
      d.addEventListener('keydown', function (e) { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); go(); } });
    });
    var prevBtn = deck.querySelector('[data-deck-prev]');
    var nextBtn = deck.querySelector('[data-deck-next]');
    if (prevBtn) prevBtn.addEventListener('click', function () { advance(-1); arm(); });
    if (nextBtn) nextBtn.addEventListener('click', function () { advance(1); arm(); });
    if (typeof IntersectionObserver === 'function') {
      new IntersectionObserver(function (entries) {
        visible = entries[0].isIntersecting;
      }, { threshold: 0.25 }).observe(deck);
    } else { visible = true; }
    /* spotlight tilt on the front card - pointer devices, carousel mode */
    if (!reduced && window.matchMedia && window.matchMedia('(hover: hover)').matches) {
      deck.addEventListener('pointermove', function (e) {
        if (deckMq && !deckMq.matches) return;
        var front = cards[order[0]];
        var card = front.querySelector('.agkb-card');
        var r = front.getBoundingClientRect();
        var px2 = (e.clientX - r.left) / r.width, py = (e.clientY - r.top) / r.height;
        if (px2 < 0 || px2 > 1 || py < 0 || py > 1) return;
        card.style.transform = 'rotateY(' + ((px2 - 0.5) * 9).toFixed(2) + 'deg) rotateX(' + ((0.5 - py) * 7).toFixed(2) + 'deg)';
        card.style.setProperty('--mx', (px2 * 100).toFixed(1) + '%');
        card.style.setProperty('--my', (py * 100).toFixed(1) + '%');
      });
      deck.addEventListener('pointerleave', function () {
        cards.forEach(function (c) {
          var card = c.querySelector('.agkb-card');
          card.style.transform = '';
        });
      });
    }
    /* The tour calls this on its last leg so the pitch lands on the first
       card. The auto-cycle is left STOPPED rather than re-armed: having
       walked someone to the buying section, moving the card out from under
       them 3 seconds later undoes the point. Any arrow, dot or swipe calls
       arm() itself, so the carousel resumes the moment they touch it. */
    deckResets.push(function () {
      if (deckMq && !deckMq.matches) return;   /* desktop grid: nothing to reset */
      order = cards.map(function (_, i) { return i; });
      paint();
      if (timer) { clearInterval(timer); timer = null; }
    });
    if (deckIsCarousel) { paint(); arm(); }
  });

  /* Desktop shows the data sheet open; the fold is a phone pattern.
     ☠ This was checked ONCE, at load. Desktop also hides the summary, so a
     window that loaded narrow and was then widened ended up with the fold
     shut AND no control to open it - the entire spec table simply absent,
     which is exactly how it was reported. Re-sync on every crossing into
     desktop. Never force it CLOSED: on phones that would undo a reader's
     own tap. */
  (function () {
    var specFold = root.querySelector('.agkb-specs .agkb-fold-dark');
    if (!specFold || !window.matchMedia) return;
    var specMq = window.matchMedia('(min-width: 901px)');
    function syncSpecFold() { if (specMq.matches) specFold.open = true; }
    syncSpecFold();
    if (specMq.addEventListener) specMq.addEventListener('change', syncSpecFold);
    else if (specMq.addListener) specMq.addListener(syncSpecFold);
  })();

  /* Guided tour button (mobile header). */
  (function () {
    var tb = root.querySelector('[data-tour]');
    if (!tb || !journey || !journey.tour) return;
    tb.addEventListener('click', function () { journey.tour(tb); });
    document.addEventListener('keydown', function () { if (journey.tourCancel) journey.tourCancel(); });
  })();

  /* Order now inside the film captions: straight to the models section. */
  Array.prototype.forEach.call(root.querySelectorAll('[data-ordernow]'), function (btn) {
    btn.addEventListener('click', function () {
      var g = GEO.map.choose;
      if (g) glideTo(g.top, 0);
    });
  });

  /* Buy now -> order builder -> cart. Two quantity steppers; every
     16 + 24 combination is auto-priced as THE PAIR and the saving is
     shown live. Checkout clears the cart (the flow owns it), adds the
     pair/single line items in ONE request, checks r.ok on both calls,
     and falls back to the product page on any failure. */
  (function () {
    var modal = root.querySelector('[data-upsell]');
    if (!modal) return;
    var ID16 = 55610590593411, ID24 = 57109901574531, PAIR_ID = 56829078307203;
    /* Server-rendered prices are the baseline; a product.js fetch refreshes
       them (and every [data-live-*] slot) so cards, note and builder always
       show the shop's LIVE price, compare-at and saving. r.ok checked;
       failure keeps the rendered values. */
    var PRICES = { 16: 109, 24: 119, pair: 225, c16: 155, c24: 190, cpair: 345 };
    function pounds0(n) { return '\u00a3' + n; }
    function paintLive() {
      var map = {
        'compare': { 16: PRICES.c16, 24: PRICES.c24, pair: PRICES.cpair },
        'price': { 16: PRICES[16], 24: PRICES[24], pair: PRICES.pair },
        'save': { 16: PRICES.c16 - PRICES[16], 24: PRICES.c24 - PRICES[24], pair: PRICES.cpair - PRICES.pair }
      };
      ['compare', 'price', 'save'].forEach(function (kind) {
        Array.prototype.forEach.call(root.querySelectorAll('[data-live-' + kind + ']'), function (el) {
          var key = el.getAttribute('data-live-' + kind);
          var v = map[kind][key];
          if (v === undefined || v <= 0) return;
          el.textContent = (kind === 'save' ? 'SAVE ' : '') + pounds0(v);
        });
      });
    }
    (function () {
      var base = (root.querySelector('[data-upsell]') || {}).getAttribute
        ? root.querySelector('[data-upsell]').getAttribute('data-fallback-href') : null;
      if (!base) return;
      fetch(base.split('?')[0] + '.js').then(function (r) {
        if (!r.ok) throw new Error('product ' + r.status);
        return r.json();
      }).then(function (prod) {
        (prod.variants || []).forEach(function (v) {
          var key = v.id === ID16 ? 16 : v.id === ID24 ? 24 : v.id === PAIR_ID ? 'pair' : null;
          if (key === null) return;
          PRICES[key] = Math.round(v.price / 100);
          var ck = key === 'pair' ? 'cpair' : 'c' + key;
          if (v.compare_at_price) PRICES[ck] = Math.round(v.compare_at_price / 100);
        });
        paintLive();
        if (typeof recalc === 'function') recalc();
      }).catch(function () {});
    })();
    var fallbackHref = modal.getAttribute('data-fallback-href') || '/cart';
    var qty = { 16: 0, 24: 0 };
    var busy = false;
    var qtyEls = { 16: modal.querySelector('[data-up-qty="16"]'), 24: modal.querySelector('[data-up-qty="24"]') };
    var totalEl = modal.querySelector('[data-up-total]');
    var saveEl = modal.querySelector('[data-up-save]');
    var savingsEl = modal.querySelector('[data-up-savings]');
    var checkoutBtn = modal.querySelector('[data-up-checkout]');
    var pairFlagEl = modal.querySelector('[data-up-pairflag]');
    function recalc() {
      var pairs = Math.min(qty[16], qty[24]);
      var total = pairs * PRICES.pair + (qty[16] - pairs) * PRICES[16] + (qty[24] - pairs) * PRICES[24];
      var compareTotal = qty[16] * PRICES.c16 + qty[24] * PRICES.c24;
      qtyEls[16].textContent = qty[16];
      qtyEls[24].textContent = qty[24];
      totalEl.textContent = pounds0(total);
      var saved = compareTotal - total;
      if (total > 0 && saved > 0) {
        savingsEl.textContent = pounds0(saved);
        if (pairFlagEl) pairFlagEl.hidden = pairs === 0;
        saveEl.hidden = false;
      } else {
        saveEl.hidden = true;
      }
      checkoutBtn.disabled = total === 0 || busy;
    }
    function openModal(chosen) {
      qty[16] = chosen === 16 || chosen === 'pair' ? 1 : 0;
      qty[24] = chosen === 24 || chosen === 'pair' ? 1 : 0;
      busy = false;
      recalc();
      modal.hidden = false;
      document.documentElement.style.overflow = 'hidden';
    }
    function closeModal() {
      modal.hidden = true;
      busy = false;
      document.documentElement.style.overflow = '';
    }
    [16, 24].forEach(function (k) {
      modal.querySelector('[data-up-plus="' + k + '"]').addEventListener('click', function () {
        if (qty[k] < 9) { qty[k] += 1; recalc(); }
      });
      modal.querySelector('[data-up-minus="' + k + '"]').addEventListener('click', function () {
        if (qty[k] > 0) { qty[k] -= 1; recalc(); }
      });
    });
    checkoutBtn.addEventListener('click', function () {
      if (busy) return;
      var pairs = Math.min(qty[16], qty[24]);
      var items = [];
      if (pairs > 0) items.push({ id: PAIR_ID, quantity: pairs });
      if (qty[16] - pairs > 0) items.push({ id: ID16, quantity: qty[16] - pairs });
      if (qty[24] - pairs > 0) items.push({ id: ID24, quantity: qty[24] - pairs });
      if (!items.length) return;
      busy = true;
      recalc();
      fetch('/cart/clear.js', { method: 'POST' }).then(function (rc) {
        if (!rc.ok) throw new Error('cart clear ' + rc.status);
        return fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: items })
        });
      }).then(function (r) {
        if (!r.ok) throw new Error('cart add ' + r.status);
        window.location.href = '/cart';
      }).catch(function () {
        window.location.href = fallbackHref;
      });
    });
    Array.prototype.forEach.call(root.querySelectorAll('[data-buy]'), function (a) {
      a.addEventListener('click', function (e) {
        e.preventDefault();
        var nm = a.getAttribute('data-buy-name') || '';
        openModal(nm === 'pair' ? 'pair' : (/16/.test(nm) ? 16 : 24));
      });
    });
    Array.prototype.forEach.call(modal.querySelectorAll('[data-upsell-close]'), function (el) {
      el.addEventListener('click', closeModal);
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !modal.hidden) closeModal();
    });
  })();

  function filmProgress(section) {
    var rect = section.getBoundingClientRect();
    var vh = window.innerHeight;
    return clamp((vh - rect.top) / section.offsetHeight, 0, 1);
  }

  var hasGsap = typeof window.gsap !== 'undefined' && typeof window.ScrollTrigger !== 'undefined';

  if (hasGsap) {
    var gsap = window.gsap;
    gsap.registerPlugin(window.ScrollTrigger);

    if (journey) {
      window.ScrollTrigger.create({
        trigger: journey.section,
        start: 'top bottom',
        end: 'bottom bottom',
        onUpdate: function () { journey.update(); },
        onLeave: function () { journey.update(); },
        onLeaveBack: function () { journey.update(); }
      });
      journey.update();
    }

    if (header) {
      window.ScrollTrigger.create({
        start: 80,
        end: 'max',
        onEnter: function () { header.classList.add('is-scrolled'); },
        onLeaveBack: function () { header.classList.remove('is-scrolled'); }
      });
    }

    /* (the rail + header marker now live in one resolver below) */

    gsap.matchMedia().add('(prefers-reduced-motion: no-preference)', function () {
      var heroSection = root.querySelector('[data-agkb="hero"]');
      var heroPhoto = root.querySelector('[data-hero-photo]');
      var heroCopy = root.querySelector('[data-hero-copy]');
      var heroDip = root.querySelector('[data-hero-dip]');
      if (heroSection && heroPhoto) {
        gsap.timeline({
          scrollTrigger: { trigger: heroSection, start: 'top top', end: 'bottom bottom', scrub: 0.4 },
          defaults: { ease: 'none' }
        })
          .to(heroPhoto, { scale: 1.12, yPercent: -4, transformOrigin: '62% 45%', duration: 1 }, 0)
          .to(heroCopy, { autoAlpha: 0, y: -36, duration: 0.35 }, 0.3)
          .to(heroDip, { opacity: 1, duration: 0.3 }, 0.45);
      }

      var cards = root.querySelectorAll('[data-card]');
      if (cards.length) {
        gsap.from(cards, {
          y: 36,
          autoAlpha: 0,
          duration: 0.7,
          ease: 'power2.out',
          stagger: 0.12,
          scrollTrigger: { trigger: root.querySelector('.agkb-cards'), start: 'top 80%', once: true }
        });
      }

      /* The data sheet lives behind a click-to-expand fold now - the
         scrubbed entrance retired with the always-visible table. */

      return function () {};
    });
    return;
  }

  /* --- Fallback without GSAP: films, captions, chapters, seq bar, header --- */
  var ticking = false;
  function update() {
    if (journey) journey.update();
    if (header) header.classList.toggle('is-scrolled', window.scrollY > 80);
  }
  addEventListener('scroll', function () {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(function () { update(); ticking = false; });
  }, { passive: true });
  addEventListener('resize', update);
  addEventListener('load', function () {
    var warmAll = function () {
      if (journey) journey.films.forEach(function (f) { if (f) f.warm(); });
    };
    if ('requestIdleCallback' in window) requestIdleCallback(warmAll);
    else setTimeout(warmAll, 200);
  });

  update();
})();
